import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { swalError } from "../../../../utils/swalAlert";
import Table from "../../../ui/Table";
import Navbar from "../../../ui/Navbar";
import "../../../../App.css";

const groupBy = (array, key) => {
  return array.reduce((result, item) => {
    const group = item[key];
    if (!result[group]) {
      result[group] = [];
    }
    result[group].push(item);
    return result;
  }, {});
};

// Model Costing - always fetched from backend AI model config
const modelCosting = (type, aiModelConfigs = {}) => {
  const config = aiModelConfigs[type] || (type === "HomeWorkGrader" ? aiModelConfigs["Grader"] : null);
  return {
    inputCostPerMillionTokens: config ? config.inputCostPerMillionTokens : 0,
    outputCostPerMillionTokens: config ? config.outputCostPerMillionTokens : 0,
    thinkingCostPerMillionTokens: config ? config.thinkingCostPerMillionTokens : 0,
  };
};

const computeTotalCost = (usageItems, type, aiModelConfigs = {}) => {
  const costData = usageItems.map((item) => {
    if (item.totalCost) return item.totalCost;
    // Fallback to calculate cost if not provided
    const {
      inputCostPerMillionTokens,
      outputCostPerMillionTokens,
      thinkingCostPerMillionTokens,
    } = modelCosting(type, aiModelConfigs);
    const costInDollars =
      ((item.inputTokenCount ?? 0) * inputCostPerMillionTokens +
        (item.outputTokenCount ?? 0) * outputCostPerMillionTokens +
        (item.thoughtsTokenCount ?? 0) * thinkingCostPerMillionTokens) /
      1000000;
    return costInDollars;
  });
  const totalCostInDollars = costData.reduce(
    (sum, cost) => sum + parseFloat(cost),
    0,
  );
  const totalCostInRupees = totalCostInDollars * 90; // Assuming 1 USD = 90 INR
  const totalCostInPaisa = totalCostInRupees * 100;
  return { totalCostInDollars, totalCostInRupees, totalCostInPaisa };
};

const computeTotalCostForAllTypes = (usageItems, aiModelConfigs = {}) => {
  const graderItems = usageItems.filter((item) => item.type === "Grader");
  const graderCost = computeTotalCost(graderItems, "Grader", aiModelConfigs).totalCostInRupees;

  const qbItems = usageItems.filter((item) => item.type === "QBGenerator");
  const qbCost = computeTotalCost(qbItems, "QBGenerator", aiModelConfigs).totalCostInRupees;

  const qbExtractorItems = usageItems.filter((item) => item.type === "QBExtractor");
  const qbExtractorCost = computeTotalCost(qbExtractorItems, "QBExtractor", aiModelConfigs).totalCostInRupees;

  const imageItems = usageItems.filter(
    (item) => item.type === "ImageGenerator",
  );
  const imageCost = computeTotalCost(
    imageItems,
    "ImageGenerator",
    aiModelConfigs,
  ).totalCostInRupees;

  const scoreSummarizerItems = usageItems.filter(
    (item) => item.type === "ScoreSummarizer",
  );
  const scoreSummarizerCost = computeTotalCost(
    scoreSummarizerItems,
    "ScoreSummarizer",
    aiModelConfigs,
  ).totalCostInRupees;

  const solutionItems = usageItems.filter(
    (item) => item.type === "SolutionGenerator",
  );
  const solutionCost = computeTotalCost(
    solutionItems,
    "SolutionGenerator",
    aiModelConfigs,
  ).totalCostInRupees;

  const homeworkGraderItems = usageItems.filter((item) => item.type === "HomeWorkGrader");
  const homeworkGraderCost = computeTotalCost(homeworkGraderItems, "HomeWorkGrader", aiModelConfigs).totalCostInRupees;

  const mcqParserItems = usageItems.filter((item) => item.type === "MCQAnswerPaperParser");
  const mcqParserCost = computeTotalCost(mcqParserItems, "MCQAnswerPaperParser", aiModelConfigs).totalCostInRupees;

  const userPromptItems = usageItems.filter((item) => item.type === "UserPromptGenerator");
  const userPromptCost = computeTotalCost(userPromptItems, "UserPromptGenerator", aiModelConfigs).totalCostInRupees;

  const allTotalCostInRupees = graderCost + homeworkGraderCost + qbCost + qbExtractorCost + imageCost + scoreSummarizerCost + solutionCost + mcqParserCost + userPromptCost;

  return { totalCostInRupees: allTotalCostInRupees };
};

const getItemCreditUsage = (item) => {
  if (item.creditUsage != null && item.creditUsage !== 0) {
    const val = Number(item.creditUsage);
    return isNaN(val) ? 1 : val;
  }
  if (
    item.type === "QBGenerator" ||
    item.type === "QBExtractor" ||
    item.type === "SolutionGenerator"
  ) {
    const qCount = Number(item.questionCount);
    return !isNaN(qCount) && qCount > 0 ? qCount : 1;
  }
  return 1;
};

const formatNumber = (num) => {
  return new Intl.NumberFormat("en-IN").format(num);
};

const getRowBatchInfo = (items) => {
  if (!items || items.length === 0) {
    return {
      isBatch: false,
      rowClassName: "",
      rowStyle: {},
    };
  }
  const isBatch = items.every((i) => Boolean(i.isExternalBatch));
  return {
    isBatch,
    rowClassName: "",
    rowStyle: {},
  };
};

const getProviderInfo = (items) => {
  if (!items || items.length === 0) return "gemini";
  const uniqueProviders = [
    ...new Set(
      items
        .map((i) => (i.provider ? String(i.provider).toLowerCase().trim() : "gemini"))
        .filter(Boolean)
    ),
  ];
  return uniqueProviders.length > 0 ? uniqueProviders.join(", ") : "gemini";
};

const renderCostWithProvider = (item) => {
  // Only display provider and batch mode badges on the last child row (leaf node)
  if (item.hasChildren !== false) {
    return <span className="cost-amount-text">₹{item.cost}</span>;
  }

  const providerText = (item.provider || "gemini").toLowerCase();
  const isMultiple = providerText.includes(",");
  let providerClass = "provider-gemini";
  if (isMultiple) {
    providerClass = "provider-mixed";
  } else if (providerText.includes("openai")) {
    providerClass = "provider-openai";
  }

  const isBatch = Boolean(item.isExternalBatch);

  return (
    <div className="cost-cell-wrapper">
      <div className="cost-primary-row">
        <span className="cost-amount-text">₹{item.cost}</span>
        <span className={`cost-provider-tag ${providerClass}`}>
          [ {providerText} ]
        </span>
      </div>
      <span className={`mode-badge-pill ${isBatch ? "mode-batch" : "mode-direct"}`}>
        {isBatch ? "Batch" : "Non-Batch"}
      </span>
    </div>
  );
};

const TokenUsageLegend = () => null;

const typeOptions = [
  { value: "All", label: "All Types", icon: "📊" },
  { value: "Grader", label: "Grader", icon: "📝" },
  { value: "HomeWorkGrader", label: "Homework Grader", icon: "📚" },
  { value: "MCQAnswerPaperParser", label: "MCQ Parser", icon: "📋" },
  { value: "QBGenerator", label: "QB Generator", icon: "❓" },
  { value: "QBExtractor", label: "QB Extractor", icon: "🔍" },
  { value: "SolutionGenerator", label: "Solution Generator", icon: "💡" },
  { value: "ImageGenerator", label: "Image Generator", icon: "🖼️" },
  { value: "ScoreSummarizer", label: "Performance Report", icon: "📈" },
  { value: "UserPromptGenerator", label: "User Prompt Generator", icon: "✍️" },
];

export default function ManageTokenUsage() {
  const [selectedType, setSelectedType] = useState("All");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [aiModelConfigs, setAiModelConfigs] = useState({});
  const { id } = useParams();
  const [showDatePopup, setShowDatePopup] = useState(false);
  const [rangeOption, setRangeOption] = useState("Today");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const breadcrumbs = [
    { name: "Home", path: "/superadmin", id: "home" },
    {
      name: "Manage Institutes",
      path: `/superadmin/manage-institutes`,
      isLast: false,
    },
    {
      name: "Token Usage",
      path: `/superadmin/manage-token-usage/${id}`,
      isLast: true,
      id: "token-usage",
    },
  ];

  const userDate = useCallback((dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toISOString().slice(0, 10);
  }, []);

  const displayDateOnly = useCallback((dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString(navigator.language, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  }, []);

  const getTimeRangeText = useCallback(() => {
    if (rangeOption === "Custom") {
      return startDate && endDate
        ? `${displayDateOnly(startDate)} - ${displayDateOnly(endDate)}`
        : "Select Date Range";
    }
    return rangeOption;
  }, [rangeOption, startDate, endDate, displayDateOnly]);

  const setTodayRange = useCallback(() => {
    const today = new Date();
    const startOfDay = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate(),
    );
    setStartDate(userDate(startOfDay));
    setEndDate(userDate(today));
    setRangeOption("Today");
  }, [userDate]);

  const handlePresetSelect = (option) => {
    setRangeOption(option);
    const now = new Date();
    let start, end;

    switch (option) {
      case "Today":
        // From today 12:00 AM to now
        start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        end = now;
        break;
      case "Last 24 Hours":
        // Rolling 24-hour window from current time
        start = new Date(now.getTime() - 24 * 60 * 60 * 1000);
        end = now;
        break;
      case "This Month":
        // From 1st of current month to now
        start = new Date(now.getFullYear(), now.getMonth(), 1);
        end = now;
        break;
      case "Custom":
      default:
        return;
    }

    const formatDate = (date) => {
      const offset = date.getTimezoneOffset();
      const localDate = new Date(date.getTime() - offset * 60000);
      return localDate.toISOString().slice(0, 10);
    };

    setStartDate(formatDate(start));
    setEndDate(formatDate(end));
  };

  const filteredData = data.filter((item) => {
    const isTypeMatch = selectedType === "All" || item.type === selectedType;
    return isTypeMatch;
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams({
        instituteId: id,
      });

      if (startDate && endDate) {
        params.append("fromDate", startDate);
        const endDateObj = new Date(endDate);
        endDateObj.setDate(endDateObj.getDate() + 1);
        const adjustedEndDate = endDateObj.toISOString().slice(0, 10);
        params.append("toDate", adjustedEndDate);
      }

      const response = await axios.get(
        `${process.env.REACT_APP_SERVER_URL}/api/token-usage/get-all?${params.toString()}`,
        { withCredentials: true },
      );
      setData(response.data);
      console.log(response.data);
    } catch (error) {
      setError(error.message);
      swalError(error.message);
    } finally {
      setLoading(false);
    }
  }, [id, startDate, endDate]);

  // Fetch AI Model Configs on mount
  const fetchAIModelConfigs = useCallback(async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_SERVER_URL}/api/institute/getAllAIModelConfig`,
        { withCredentials: true },
      );
      // Build a lookup map keyed by type (e.g., "Grader", "QBGenerator")
      const configMap = {};
      if (Array.isArray(response.data)) {
        response.data.forEach((config) => {
          if (config.type) {
            configMap[config.type] = config;
          }
        });
      }
      setAiModelConfigs(configMap);
    } catch (err) {
      console.error("Failed to fetch AI Model Configs:", err.message);
    }
  }, []);

  useEffect(() => {
    fetchAIModelConfigs();
  }, [fetchAIModelConfigs]);

  useEffect(() => {
    setTodayRange();
  }, [setTodayRange]);

  useEffect(() => {
    if (startDate && endDate) {
      fetchData();
    }
  }, [fetchData, startDate, endDate]);

  const toggleExpand = (key) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  const getStatistics = () => {
    const totalInputTokens = filteredData.reduce(
      (sum, item) => sum + (item.inputTokenCount || 0),
      0,
    );
    const totalOutputTokens = filteredData.reduce(
      (sum, item) => sum + (item.outputTokenCount || 0),
      0,
    );
    const totalTokens = filteredData.reduce(
      (sum, item) => sum + (item.totalTokenCount || 0),
      0,
    );
    const totalCreditsUsed = filteredData.reduce(
      (sum, item) => sum + getItemCreditUsage(item),
      0,
    );
    // const totalCost = getCost(totalInputTokens, totalOutputTokens);
    const { totalCostInRupees } = computeTotalCostForAllTypes(filteredData, aiModelConfigs);

    const averageCostPerCredit =
      totalCreditsUsed > 0
        ? (totalCostInRupees / totalCreditsUsed).toFixed(2)
        : "0.00";

    return {
      totalInputTokens,
      totalOutputTokens,
      totalTokens,
      totalCreditsUsed,
      totalCost: totalCostInRupees.toFixed(2),
      averageCostPerCredit,
    };
  };

  const stats = getStatistics();

  if (loading) {
    return (
      <>
        <Navbar breadcrumbs={breadcrumbs} />
        <div className="manage-token">
          <div className="loading-container">
            <div className="spinner"></div>
            <p>Loading token usage data...</p>
          </div>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Navbar breadcrumbs={breadcrumbs} />
        <div className="manage-token">
          <div className="error-container">
            <div className="error-icon">⚠️</div>
            <h2>Error Loading Data</h2>
            <p>{error}</p>
            <button onClick={fetchData} className="retry-btn">
              <span>🔄</span> Retry
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar breadcrumbs={breadcrumbs} />
      <div className="manage-token">
        <motion.div
          className="controls-section"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <div className="type-selector">
            <label>Usage Type:</label>
            <div className="type-buttons">
              {typeOptions.map((option) => (
                <button
                  key={option.value}
                  className={`type-btn ${selectedType === option.value ? "active" : ""
                    }`}
                  onClick={() => setSelectedType(option.value)}
                >
                  <span className="type-icon">{option.icon}</span>
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </motion.div>

        <div className="time-range-wrapper">
          <button
            onClick={() => setShowDatePopup((prev) => !prev)}
            className="time-range-btn"
            aria-expanded={showDatePopup}
            aria-haspopup="true"
          >
            <span className="btn-icon">🕒</span>
            {getTimeRangeText()}
            <span className={`chevron ${showDatePopup ? "open" : ""}`}>▼</span>
          </button>

          {showDatePopup && (
            <>
              <div
                className="popup-overlay"
                onClick={() => setShowDatePopup(false)}
              />
              <div className="time-range-popup">
                <div className="popup-header">
                  <h3>Select Time Range</h3>
                  <button
                    className="close-btn"
                    onClick={() => setShowDatePopup(false)}
                    aria-label="Close"
                  >
                    ✕
                  </button>
                </div>

                <div className="preset-section">
                  <div className="preset-grid">
                    {["Today", "Last 24 Hours", "This Month", "Custom"].map(
                      (option) => (
                        <button
                          key={option}
                          className={`preset-option ${rangeOption === option ? "selected" : ""
                            }`}
                          onClick={() => handlePresetSelect(option)}
                        >
                          {option}
                        </button>
                      ),
                    )}
                  </div>
                </div>

                <div className="custom-range-section">
                  <div className="date-inputs">
                    <div className="input-group">
                      <label htmlFor="start-date">Start Date</label>
                      <input
                        id="start-date"
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        className="date-input"
                      />
                    </div>
                    <div className="input-group">
                      <label htmlFor="end-date">End Date</label>
                      <input
                        id="end-date"
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        className="date-input"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        <motion.div
          className="stats-grid"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          <div className="stat-card">
            <div className="stat-icon input">📥</div>
            <div className="stat-content">
              <h3>Input Tokens</h3>
              <p className="stat-number">
                {formatNumber(stats.totalInputTokens)}
              </p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon output">📤</div>
            <div className="stat-content">
              <h3>Output Tokens</h3>
              <p className="stat-number">
                {formatNumber(stats.totalOutputTokens)}
              </p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon total">📊</div>
            <div className="stat-content">
              <h3>Total Tokens</h3>
              <p className="stat-number">{formatNumber(stats.totalTokens)}</p>
            </div>
          </div>
          <div
            className="stat-card"
            title={
              stats.totalCreditsUsed > 0
                ? `Average Cost: ₹${stats.averageCostPerCredit} per credit`
                : "Total Credits Used"
            }
          >
            <div className="stat-icon credits">🪙</div>
            <div className="stat-content">
              <h3>Total Credits Used</h3>
              <p className="stat-number">
                {formatNumber(stats.totalCreditsUsed)}
              </p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon cost">💰</div>
            <div className="stat-content">
              <h3>Total Cost</h3>
              <p className="stat-number">₹{stats.totalCost}</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          className="table-section"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <AnimatePresence mode="wait">
            {selectedType === "All" && (
              <AllTokenUsageTable
                key="all"
                data={filteredData}
                expandedRows={expandedRows}
                toggleExpand={toggleExpand}
                aiModelConfigs={aiModelConfigs}
              />
            )}
            {selectedType === "Grader" && (
              <GraderTable
                key="grader"
                data={filteredData}
                expandedRows={expandedRows}
                toggleExpand={toggleExpand}
                aiModelConfigs={aiModelConfigs}
                usageType="Grader"
              />
            )}
            {selectedType === "HomeWorkGrader" && (
              <GraderTable
                key="homeworkGrader"
                data={filteredData}
                expandedRows={expandedRows}
                toggleExpand={toggleExpand}
                aiModelConfigs={aiModelConfigs}
                usageType="HomeWorkGrader"
              />
            )}
            {selectedType === "QBGenerator" && (
              <QBGeneratorTable
                key="qb"
                data={filteredData}
                expandedRows={expandedRows}
                toggleExpand={toggleExpand}
                aiModelConfigs={aiModelConfigs}
              />
            )}
            {selectedType === "QBExtractor" && (
              <QBExtractorTable
                key="qbExtractor"
                data={filteredData}
                expandedRows={expandedRows}
                toggleExpand={toggleExpand}
                aiModelConfigs={aiModelConfigs}
              />
            )}
            {selectedType === "SolutionGenerator" && (
              <SolutionGeneratorTable
                key="solution"
                data={filteredData}
                expandedRows={expandedRows}
                toggleExpand={toggleExpand}
                aiModelConfigs={aiModelConfigs}
              />
            )}
            {selectedType === "ImageGenerator" && (
              <ImageGeneratorTable
                key="image"
                data={filteredData}
                expandedRows={expandedRows}
                toggleExpand={toggleExpand}
                aiModelConfigs={aiModelConfigs}
              />
            )}
            {selectedType === "ScoreSummarizer" && (
              <ScoreSummarizerTable
                key="scoreSummarizer"
                data={filteredData}
                expandedRows={expandedRows}
                toggleExpand={toggleExpand}
                aiModelConfigs={aiModelConfigs}
              />
            )}
            {selectedType === "MCQAnswerPaperParser" && (
              <MCQAnswerPaperParserTable
                key="mcqParser"
                data={filteredData}
                expandedRows={expandedRows}
                toggleExpand={toggleExpand}
                aiModelConfigs={aiModelConfigs}
              />
            )}
            {selectedType === "UserPromptGenerator" && (
              <UserPromptGeneratorTable
                key="userPrompt"
                data={filteredData}
                expandedRows={expandedRows}
                toggleExpand={toggleExpand}
                aiModelConfigs={aiModelConfigs}
              />
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </>
  );
}

const AllTokenUsageTable = ({ data, expandedRows, toggleExpand, aiModelConfigs }) => {
  const instructorGroups = groupBy(data, "instructorId");

  const tableData = Object.entries(instructorGroups).map(
    ([instructorId, items]) => {
      const totalTokens = items.reduce(
        (acc, item) => {
          acc.input += item.inputTokenCount;
          acc.output += item.outputTokenCount;
          acc.thoughts += item.thoughtsTokenCount ?? 0;
          acc.total += item.totalTokenCount;
          return acc;
        },
        { input: 0, output: 0, thoughts: 0, total: 0 },
      );
      const { totalCostInRupees } = computeTotalCostForAllTypes(items, aiModelConfigs);

      const rowBatchInfo = getRowBatchInfo(items);
      const rowProvider = getProviderInfo(items);

      return {
        _id: instructorId,
        instructorName: items[0].instructorName || "[DELETED]",
        inputTokens: totalTokens.input,
        outputTokens: totalTokens.output,
        thoughtsTokens: totalTokens.thoughts,
        totalTokens: totalTokens.total,
        cost: totalCostInRupees.toFixed(2),
        itemCount: items.length,
        isExternalBatch: rowBatchInfo.isBatch,
        provider: rowProvider,
        _rowClassName: rowBatchInfo.rowClassName,
        _rowStyle: rowBatchInfo.rowStyle,
      };
    },
  );
  // TableColumns
  const columns = [
    {
      key: "instructorName",
      label: "Instructor",
      sortable: true,
      filter: true,
      render: (item) => (
        <div className="instructor-cell">
          <span className="instructor-name">{item.instructorName}</span>
          <span className="item-count">{item.itemCount} records</span>
        </div>
      ),
    },
    {
      key: "inputTokens",
      label: "Input Tokens",
      sortable: true,
      render: (item) => (
        <span className="token-count input">
          {formatNumber(item.inputTokens)}
        </span>
      ),
    },
    {
      key: "outputTokens",
      label: "Output Tokens",
      sortable: true,
      render: (item) => (
        <span className="token-count output">
          {formatNumber(item.outputTokens)}
        </span>
      ),
    },
    {
      key: "thoughtsTokens",
      label: "Thoughts Tokens",
      sortable: true,
      render: (item) => (
        <span className="token-count output">
          {formatNumber(item.thoughtsTokens)}
        </span>
      ),
    },
    {
      key: "totalTokens",
      label: "Total Tokens",
      sortable: true,
      render: (item) => (
        <span className="token-count total">
          {formatNumber(item.totalTokens)}
        </span>
      ),
    },
    {
      key: "cost",
      label: "Cost (₹)",
      sortable: true,
      render: (item) => renderCostWithProvider(item),
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
    >
      <TokenUsageLegend />
      <Table
        data={tableData}
        columns={columns}
        caption="Token Usage Overview by Instructor"
      />
    </motion.div>
  );
};

const GraderTable = ({ data, expandedRows, toggleExpand, aiModelConfigs, usageType = "Grader" }) => {
  const instructorGroups = groupBy(data, "instructorId");

  const tableData = [];

  Object.entries(instructorGroups).forEach(([instructorId, items]) => {
    // 1. Instructor Level Aggregation
    const instructorItems = items;
    const totalTokens = instructorItems.reduce(
      (acc, item) => {
        acc.input += item.inputTokenCount;
        acc.cachedInput += item.cachedInputTokenCount ?? 0;
        acc.output += item.outputTokenCount;
        acc.thoughts += item.thoughtsTokenCount ?? 0;
        acc.total += item.totalTokenCount;
        acc.creditUsage +=
          item.creditUsage != null && item.creditUsage !== 0
            ? item.creditUsage
            : 1;
        acc.filePageCount += item.filePageCount ?? 0;
        return acc;
      },
      {
        input: 0,
        cachedInput: 0,
        output: 0,
        thoughts: 0,
        total: 0,
        creditUsage: 0,
        filePageCount: 0,
      },
    );
    const { totalCostInRupees } = computeTotalCost(instructorItems, usageType, aiModelConfigs);

    const instBatchInfo = getRowBatchInfo(instructorItems);
    const instProvider = getProviderInfo(instructorItems);

    tableData.push({
      _id: instructorId,
      type: "instructor",
      instructorName: instructorItems[0].instructorName || "[DELETED]",
      testName: "",
      testType: "",
      studentName: "",
      className: "", // Empty for Instructor level
      scoreId: "",
      cachedInputTokens: totalTokens.cachedInput,
      inputTokens: totalTokens.input,
      outputTokens: totalTokens.output,
      thoughtsTokens: totalTokens.thoughts,
      creditUsage: totalTokens.creditUsage,
      filePageCount: totalTokens.filePageCount,
      totalTokens: totalTokens.total,
      cost: totalCostInRupees.toFixed(2),
      isExternalBatch: instBatchInfo.isBatch,
      provider: instProvider,
      _rowClassName: instBatchInfo.rowClassName,
      _rowStyle: instBatchInfo.rowStyle,
      level: 0,
      parentId: null,
      hasChildren: true,
      isExpanded: expandedRows.has(instructorId),
    });

    if (expandedRows.has(instructorId)) {
      // 2. Class Level Grouping
      const classGroups = groupBy(instructorItems, "className");
      Object.entries(classGroups).forEach(([className, classItems]) => {
        const classLevelId = `${instructorId}_${className}`; // Unique ID for Class level
        const classTokens = classItems.reduce(
          (acc, item) => {
            acc.input += item.inputTokenCount;
            acc.cachedInput += item.cachedInputTokenCount ?? 0;
            acc.output += item.outputTokenCount;
            acc.thoughts += item.thoughtsTokenCount ?? 0;
            acc.total += item.totalTokenCount;
            acc.creditUsage +=
              item.creditUsage != null && item.creditUsage !== 0
                ? item.creditUsage
                : 1;
            acc.filePageCount += item.filePageCount ?? 0;
            return acc;
          },
          {
            input: 0,
            cachedInput: 0,
            output: 0,
            thoughts: 0,
            total: 0,
            creditUsage: 0,
            filePageCount: 0,
          },
        );
        const { totalCostInRupees } = computeTotalCost(classItems, usageType, aiModelConfigs);
        const classBatchInfo = getRowBatchInfo(classItems);
        const classProvider = getProviderInfo(classItems);

        tableData.push({
          _id: classLevelId,
          type: "class",
          instructorName: "",
          testName: "",
          testType: "",
          studentName: "",
          className: className || "[DELETED]",
          scoreId: "",
          cachedInputTokens: classTokens.cachedInput,
          inputTokens: classTokens.input,
          outputTokens: classTokens.output,
          thoughtsTokens: classTokens.thoughts,
          totalTokens: classTokens.total,
          creditUsage: classTokens.creditUsage,
          filePageCount: classTokens.filePageCount,
          cost: totalCostInRupees.toFixed(2),
          isExternalBatch: classBatchInfo.isBatch,
          provider: classProvider,
          _rowClassName: classBatchInfo.rowClassName,
          _rowStyle: classBatchInfo.rowStyle,
          level: 1,
          parentId: instructorId,
          hasChildren: true,
          isExpanded: expandedRows.has(classLevelId),
        });

        if (expandedRows.has(classLevelId)) {
          // 3. Test Level Grouping (Under Class)
          const testGroups = groupBy(classItems, "testId");
          Object.entries(testGroups).forEach(([testId, testItems]) => {
            const testLevelId = `${classLevelId}_${testId}`; // Unique ID for Test level
            const testTokens = testItems.reduce(
              (acc, item) => {
                acc.input += item.inputTokenCount;
                acc.cachedInput += item.cachedInputTokenCount ?? 0;
                acc.output += item.outputTokenCount;
                acc.thoughts += item.thoughtsTokenCount ?? 0;
                acc.total += item.totalTokenCount;
                acc.creditUsage +=
                  item.creditUsage != null && item.creditUsage !== 0
                    ? item.creditUsage
                    : 1;
                acc.filePageCount += item.filePageCount ?? 0;
                return acc;
              },
              {
                input: 0,
                cachedInput: 0,
                output: 0,
                thoughts: 0,
                total: 0,
                creditUsage: 0,
                filePageCount: 0,
              },
            );
            const { totalCostInRupees } = computeTotalCost(testItems, usageType, aiModelConfigs);
            const testBatchInfo = getRowBatchInfo(testItems);
            const testProvider = getProviderInfo(testItems);

            tableData.push({
              _id: testLevelId,
              type: "test",
              instructorName: "",
              testName: testItems[0].testName || "[DELETED]",
              testType: testItems[0].testType || "",
              className: "", // Empty for Test level
              studentName: "",
              scoreId: "",
              cachedInputTokens: testTokens.cachedInput,
              inputTokens: testTokens.input,
              outputTokens: testTokens.output,
              thoughtsTokens: testTokens.thoughts,
              totalTokens: testTokens.total,
              creditUsage: testTokens.creditUsage,
              filePageCount: testTokens.filePageCount,
              cost: totalCostInRupees.toFixed(2),
              isExternalBatch: testBatchInfo.isBatch,
              provider: testProvider,
              _rowClassName: testBatchInfo.rowClassName,
              _rowStyle: testBatchInfo.rowStyle,
              level: 2,
              parentId: classLevelId,
              hasChildren: true,
              isExpanded: expandedRows.has(testLevelId),
            });

            if (expandedRows.has(testLevelId)) {
              // 4. Student Level Grouping (Under Test)
              const studentGroups = groupBy(testItems, "studentId");
              Object.entries(studentGroups).forEach(
                ([studentId, studentItems]) => {
                  const studentLevelId = `${testLevelId}_${studentId}`; // Unique ID for Student level
                  const studentTokens = studentItems.reduce(
                    (acc, item) => {
                      acc.input += item.inputTokenCount;
                      acc.cachedInput += item.cachedInputTokenCount ?? 0;
                      acc.output += item.outputTokenCount;
                      acc.thoughts += item.thoughtsTokenCount ?? 0;
                      acc.total += item.totalTokenCount;
                      acc.creditUsage +=
                        item.creditUsage != null && item.creditUsage !== 0
                          ? item.creditUsage
                          : 1;
                      acc.filePageCount += item.filePageCount ?? 0;
                      return acc;
                    },
                    {
                      input: 0,
                      cachedInput: 0,
                      output: 0,
                      thoughts: 0,
                      total: 0,
                      creditUsage: 0,
                      filePageCount: 0,
                    },
                  );
                  const { totalCostInRupees } = computeTotalCost(
                    studentItems,
                    usageType,
                    aiModelConfigs,
                  );
                  const studentBatchInfo = getRowBatchInfo(studentItems);
                  const studentProvider = getProviderInfo(studentItems);

                  tableData.push({
                    _id: studentLevelId,
                    type: "student",
                    instructorName: "",
                    testName: "",
                    testType: "",
                    studentName: studentItems[0].studentName || "[DELETED]",
                    className: "", // Empty for Student level
                    scoreId: "",
                    cachedInputTokens: studentTokens.cachedInput,
                    inputTokens: studentTokens.input,
                    outputTokens: studentTokens.output,
                    thoughtsTokens: studentTokens.thoughts,
                    totalTokens: studentTokens.total,
                    creditUsage: studentTokens.creditUsage,
                    filePageCount: studentTokens.filePageCount,
                    cost: totalCostInRupees.toFixed(2),
                    isExternalBatch: studentBatchInfo.isBatch,
                    provider: studentProvider,
                    _rowClassName: studentBatchInfo.rowClassName,
                    _rowStyle: studentBatchInfo.rowStyle,
                    level: 3,
                    parentId: testLevelId,
                    hasChildren: true,
                    isExpanded: expandedRows.has(studentLevelId),
                  });

                  if (expandedRows.has(studentLevelId)) {
                    // 5. Score Level (Under Student)
                    studentItems.forEach((item) => {
                      const { totalCostInRupees } = computeTotalCost(
                        [item],
                        usageType,
                        aiModelConfigs,
                      );
                      const creditUsage =
                        item.creditUsage != null && item.creditUsage !== 0
                          ? item.creditUsage
                          : 1;
                      const scoreBatchInfo = getRowBatchInfo([item]);
                      const scoreProvider = getProviderInfo([item]);

                      tableData.push({
                        _id: item.scoreId,
                        type: "score",
                        instructorName: "",
                        testName: "",
                        testType: "",
                        studentName: "",
                        scoreId: item.scoreId,
                        createdAt: item.createdAt,
                        className: "", // Empty for Score level
                        cachedInputTokens: item.cachedInputTokenCount ?? 0,
                        inputTokens: item.inputTokenCount,
                        outputTokens: item.outputTokenCount,
                        thoughtsTokens: item.thoughtsTokenCount,
                        totalTokens: item.totalTokenCount,
                        creditUsage: creditUsage,
                        filePageCount: item.filePageCount,
                        cost: totalCostInRupees.toFixed(2),
                        isExternalBatch: scoreBatchInfo.isBatch,
                        provider: scoreProvider,
                        _rowClassName: scoreBatchInfo.rowClassName,
                        _rowStyle: scoreBatchInfo.rowStyle,
                        level: 4, // New level
                        parentId: studentLevelId,
                        hasChildren: false,
                        isExpanded: false,
                      });
                    });
                  }
                },
              );
            }
          });
        }
      });
    }
  });

  const columns = [
    {
      key: "instructorName",
      label: "Instructor",
      render: (item) => (
        <div
          className={`hierarchical-cell level-${item.level} ${item.hasChildren ? "expandable" : ""
            }`}
          onClick={() =>
            item.hasChildren &&
            item.type === "instructor" &&
            toggleExpand(item._id)
          }
          style={{ paddingLeft: `${item.level * 20 + 10}px` }}
        >
          {item.hasChildren && item.type === "instructor" && (
            <span
              className={`expand-icon ${item.isExpanded ? "expanded" : ""}`}
            >
              ▶
            </span>
          )}
          <span>{item.instructorName}</span>
        </div>
      ),
    },
    {
      key: "className",
      label: "Class",
      render: (item) => (
        <div
          className={`hierarchical-cell level-${item.level} ${item.hasChildren ? "expandable" : ""
            }`}
          onClick={() =>
            item.hasChildren && item.type === "class" && toggleExpand(item._id)
          }
        >
          {item.type === "class" && item.hasChildren && (
            <span
              className={`expand-icon ${item.isExpanded ? "expanded" : ""}`}
            >
              ▶
            </span>
          )}
          <span>{item.className}</span>
        </div>
      ),
    },
    {
      key: "testName",
      label: "Test",
      render: (item) => (
        <div
          className={`hierarchical-cell level-${item.level} ${item.hasChildren ? "expandable" : ""
            }`}
          onClick={() =>
            item.hasChildren && item.type === "test" && toggleExpand(item._id)
          }
        >
          {item.type === "test" && item.hasChildren && (
            <span
              className={`expand-icon ${item.isExpanded ? "expanded" : ""}`}
            >
              ▶
            </span>
          )}
          <span>
            {item.testName}
            {item.testType ? ` (${item.testType})` : ""}
          </span>
        </div>
      ),
    },
    {
      key: "studentName",
      label: "Student",
      render: (item) => (
        <div
          className={`hierarchical-cell level-${item.level} ${item.hasChildren ? "expandable" : ""
            }`}
          onClick={() =>
            item.hasChildren &&
            item.type === "student" &&
            toggleExpand(item._id)
          }
        >
          {item.type === "student" && item.hasChildren && (
            <span
              className={`expand-icon ${item.isExpanded ? "expanded" : ""}`}
            >
              ▶
            </span>
          )}
          <span>{item.studentName}</span>
        </div>
      ),
    },
    { key: "scoreId", label: "Score ID" },
    {
      key: "date",
      label: "Date",
      render: (item) => (
        <span>
          {item.createdAt ? new Date(item.createdAt).toLocaleString() : ""}
        </span>
      ),
    },
    {
      key: "creditUsage",
      label: "Credits Used",
      render: (item) => (
        <span className="token-count">{formatNumber(item.creditUsage)}</span>
      ),
    },
    {
      key: "filePageCount",
      label: "Page Count",
      render: (item) => (
        <span className="token-count">{formatNumber(item.filePageCount)}</span>
      ),
    },
    {
      key: "cachedInputTokens",
      label: "Cached Input Tokens",
      render: (item) => (
        <span className="token-count input">
          {formatNumber(item.cachedInputTokens ?? 0)}
        </span>
      ),
    },
    {
      key: "inputTokens",
      label: "Input Tokens",
      render: (item) => (
        <span className="token-count input">
          {formatNumber(item.inputTokens)}
        </span>
      ),
    },
    {
      key: "outputTokens",
      label: "Output Tokens",
      render: (item) => (
        <span className="token-count output">
          {formatNumber(item.outputTokens)}
        </span>
      ),
    },
    {
      key: "thoughtsTokens",
      label: "Thoughts Tokens",
      render: (item) => (
        <span className="token-count output">
          {formatNumber(item.thoughtsTokens)}
        </span>
      ),
    },
    {
      key: "totalTokens",
      label: "Total Tokens",
      render: (item) => (
        <span className="token-count total">
          {formatNumber(item.totalTokens)}
        </span>
      ),
    },
    {
      key: "cost",
      label: "Cost (₹)",
      render: (item) => renderCostWithProvider(item),
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
    >
      <TokenUsageLegend />
      <Table
        data={tableData}
        columns={columns}
        caption="Grader Token Usage - Hierarchical View"
      />
    </motion.div>
  );
};

const QBGeneratorTable = ({ data, expandedRows, toggleExpand, aiModelConfigs }) => {
  const instructorGroups = groupBy(data, "instructorId");

  const tableData = [];

  Object.entries(instructorGroups).forEach(([instructorId, items]) => {
    // Level 0: Instructor Aggregation
    const instructorItems = items;
    const totalTokens = instructorItems.reduce(
      (acc, item) => {
        acc.input += item.inputTokenCount;
        acc.cachedInput += item.cachedInputTokenCount ?? 0;
        acc.output += item.outputTokenCount;
        acc.thoughts += item.thoughtsTokenCount ?? 0;
        acc.total += item.totalTokenCount;
        acc.qcount += item.questionCount;
        return acc;
      },
      { input: 0, cachedInput: 0, output: 0, thoughts: 0, total: 0, qcount: 0 },
    );
    const { totalCostInRupees } = computeTotalCost(
      instructorItems,
      "QBGenerator",
      aiModelConfigs,
    );

    const instBatchInfo = getRowBatchInfo(instructorItems);
    const instProvider = getProviderInfo(instructorItems);

    tableData.push({
      _id: instructorId,
      type: "instructor",
      instructorName: instructorItems[0].instructorName || "[DELETED]",
      className: "", // Empty for Instructor level
      questionBankId: "",
      questionBankName: "",
      questionCount: totalTokens.qcount,
      cachedInputTokens: totalTokens.cachedInput,
      inputTokens: totalTokens.input,
      outputTokens: totalTokens.output,
      thoughtsTokens: totalTokens.thoughts,
      totalTokens: totalTokens.total,
      cost: totalCostInRupees.toFixed(2),
      isExternalBatch: instBatchInfo.isBatch,
      provider: instProvider,
      _rowClassName: instBatchInfo.rowClassName,
      _rowStyle: instBatchInfo.rowStyle,
      level: 0,
      hasChildren: true,
      isExpanded: expandedRows.has(instructorId),
    });

    if (expandedRows.has(instructorId)) {
      // Level 1: Class Grouping
      const classGroups = groupBy(instructorItems, "className");
      Object.entries(classGroups).forEach(([className, classItems]) => {
        const classLevelId = `${instructorId}_${className}`; // Unique ID for Class level

        const classTokens = classItems.reduce(
          (acc, item) => {
            acc.input += item.inputTokenCount;
            acc.cachedInput += item.cachedInputTokenCount ?? 0;
            acc.output += item.outputTokenCount;
            acc.thoughts += item.thoughtsTokenCount ?? 0;
            acc.total += item.totalTokenCount;
            acc.qcount += item.questionCount ?? 0;
            return acc;
          },
          { input: 0, cachedInput: 0, output: 0, thoughts: 0, total: 0, qcount: 0 },
        );
        const { totalCostInRupees } = computeTotalCost(
          classItems,
          "QBGenerator",
          aiModelConfigs,
        );
        const classBatchInfo = getRowBatchInfo(classItems);
        const classProvider = getProviderInfo(classItems);

        tableData.push({
          _id: classLevelId,
          type: "class",
          instructorName: "",
          className: className || "[DELETED]",
          questionBankId: "",
          questionBankName: "",
          questionCount: classTokens.qcount,
          cachedInputTokens: classTokens.cachedInput,
          inputTokens: classTokens.input,
          outputTokens: classTokens.output,
          thoughtsTokens: classTokens.thoughts,
          totalTokens: classTokens.total,
          cost: totalCostInRupees.toFixed(2),
          isExternalBatch: classBatchInfo.isBatch,
          provider: classProvider,
          _rowClassName: classBatchInfo.rowClassName,
          _rowStyle: classBatchInfo.rowStyle,
          level: 1,
          hasChildren: true,
          isExpanded: expandedRows.has(classLevelId),
        });

        if (expandedRows.has(classLevelId)) {
          // Level 2: Question Bank Grouping (Leaf node)
          const qbGroups = groupBy(classItems, "questionBankId");
          Object.entries(qbGroups).forEach(([questionBankId, qbItems]) => {
            const qbTokens = qbItems.reduce(
              (acc, item) => {
                acc.input += item.inputTokenCount;
                acc.cachedInput += item.cachedInputTokenCount ?? 0;
                acc.output += item.outputTokenCount;
                acc.thoughts += item.thoughtsTokenCount ?? 0;
                acc.total += item.totalTokenCount;
                acc.qcount += item.questionCount ?? 0;
                return acc;
              },
              { input: 0, cachedInput: 0, output: 0, thoughts: 0, total: 0, qcount: 0 },
            );
            const { totalCostInRupees } = computeTotalCost(
              qbItems,
              "QBGenerator",
              aiModelConfigs,
            );
            const qbBatchInfo = getRowBatchInfo(qbItems);
            const qbProvider = getProviderInfo(qbItems);

            tableData.push({
              _id: questionBankId,
              type: "questionBank",
              instructorName: "",
              questionBankId: questionBankId,
              questionBankName: qbItems[0].questionBankName || "[DELETED]",
              className: "", // Empty for Question Bank level
              questionCount: qbItems[0].questionCount || 0,
              createdAt: qbItems[0].createdAt,
              cachedInputTokens: qbTokens.cachedInput,
              inputTokens: qbTokens.input,
              outputTokens: qbTokens.output,
              thoughtsTokens: qbTokens.thoughts,
              totalTokens: qbTokens.total,
              cost: totalCostInRupees.toFixed(2),
              isExternalBatch: qbBatchInfo.isBatch,
              provider: qbProvider,
              _rowClassName: qbBatchInfo.rowClassName,
              _rowStyle: qbBatchInfo.rowStyle,
              level: 2,
              hasChildren: false,
              isExpanded: false,
            });
          });
        }
      });
    }
  });

  const columns = [
    {
      key: "instructorName",
      label: "Instructor",
      render: (item) => (
        <div
          className={`hierarchical-cell level-${item.level} ${item.hasChildren ? "expandable" : ""
            }`}
          onClick={() =>
            item.hasChildren &&
            item.type === "instructor" &&
            toggleExpand(item._id)
          }
          style={{ paddingLeft: `${item.level * 20 + 10}px` }}
        >
          {item.hasChildren && item.type === "instructor" && (
            <span
              className={`expand-icon ${item.isExpanded ? "expanded" : ""}`}
            >
              ▶
            </span>
          )}
          <span>{item.instructorName}</span>
        </div>
      ),
    },
    {
      key: "className",
      label: "Class",
      render: (item) => (
        <div
          className={`hierarchical-cell level-${item.level} ${item.hasChildren ? "expandable" : ""
            }`}
          onClick={() =>
            item.hasChildren && item.type === "class" && toggleExpand(item._id)
          }
        >
          {item.type === "class" && item.hasChildren && (
            <span
              className={`expand-icon ${item.isExpanded ? "expanded" : ""}`}
            >
              ▶
            </span>
          )}
          <span>{item.className}</span>
        </div>
      ),
    },
    { key: "questionBankId", label: "QB ID" },
    { key: "questionBankName", label: "QB Name" },
    { key: "questionCount", label: "Question Count" },
    {
      key: "date",
      label: "Date",
      render: (item) => (
        <span>
          {item.createdAt ? new Date(item.createdAt).toLocaleString() : ""}
        </span>
      ),
    },
    {
      key: "cachedInputTokens",
      label: "Cached Input Tokens",
      render: (item) => (
        <span className="token-count input">
          {formatNumber(item.cachedInputTokens ?? 0)}
        </span>
      ),
    },
    {
      key: "inputTokens",
      label: "Input Tokens",
      render: (item) => (
        <span className="token-count input">
          {formatNumber(item.inputTokens)}
        </span>
      ),
    },
    {
      key: "outputTokens",
      label: "Output Tokens",
      render: (item) => (
        <span className="token-count output">
          {formatNumber(item.outputTokens)}
        </span>
      ),
    },
    {
      key: "thoughtsTokens",
      label: "Thoughts Tokens",
      render: (item) => (
        <span className="token-count output">
          {formatNumber(item.thoughtsTokens)}
        </span>
      ),
    },
    {
      key: "totalTokens",
      label: "Total Tokens",
      render: (item) => (
        <span className="token-count total">
          {formatNumber(item.totalTokens)}
        </span>
      ),
    },
    {
      key: "cost",
      label: "Cost (₹)",
      render: (item) => renderCostWithProvider(item),
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
    >
      <TokenUsageLegend />
      <Table
        data={tableData}
        columns={columns}
        caption="Question Bank Generator Token Usage"
      />
    </motion.div>
  );
};

const QBExtractorTable = ({ data, expandedRows, toggleExpand, aiModelConfigs }) => {
  const instructorGroups = groupBy(data, "instructorId");

  const tableData = [];

  Object.entries(instructorGroups).forEach(([instructorId, items]) => {
    // Level 0: Instructor Aggregation
    const instructorItems = items;
    const totalTokens = instructorItems.reduce(
      (acc, item) => {
        acc.input += item.inputTokenCount;
        acc.cachedInput += item.cachedInputTokenCount ?? 0;
        acc.output += item.outputTokenCount;
        acc.thoughts += item.thoughtsTokenCount ?? 0;
        acc.total += item.totalTokenCount;
        acc.qcount += item.questionCount;
        return acc;
      },
      { input: 0, cachedInput: 0, output: 0, thoughts: 0, total: 0, qcount: 0 },
    );
    const { totalCostInRupees } = computeTotalCost(
      instructorItems,
      "QBExtractor",
      aiModelConfigs,
    );

    const instBatchInfo = getRowBatchInfo(instructorItems);
    const instProvider = getProviderInfo(instructorItems);

    tableData.push({
      _id: instructorId,
      type: "instructor",
      instructorName: instructorItems[0].instructorName || "[DELETED]",
      className: "", // Empty for Instructor level
      questionBankId: "",
      questionBankName: "",
      questionCount: totalTokens.qcount,
      cachedInputTokens: totalTokens.cachedInput,
      inputTokens: totalTokens.input,
      outputTokens: totalTokens.output,
      thoughtsTokens: totalTokens.thoughts,
      totalTokens: totalTokens.total,
      cost: totalCostInRupees.toFixed(2),
      isExternalBatch: instBatchInfo.isBatch,
      provider: instProvider,
      _rowClassName: instBatchInfo.rowClassName,
      _rowStyle: instBatchInfo.rowStyle,
      level: 0,
      hasChildren: true,
      isExpanded: expandedRows.has(instructorId),
    });

    if (expandedRows.has(instructorId)) {
      // Level 1: Class Grouping
      const classGroups = groupBy(instructorItems, "className");
      Object.entries(classGroups).forEach(([className, classItems]) => {
        const classLevelId = `${instructorId}_${className}`; // Unique ID for Class level

        const classTokens = classItems.reduce(
          (acc, item) => {
            acc.input += item.inputTokenCount;
            acc.cachedInput += item.cachedInputTokenCount ?? 0;
            acc.output += item.outputTokenCount;
            acc.thoughts += item.thoughtsTokenCount ?? 0;
            acc.total += item.totalTokenCount;
            acc.qcount += item.questionCount ?? 0;
            return acc;
          },
          { input: 0, cachedInput: 0, output: 0, thoughts: 0, total: 0, qcount: 0 },
        );
        const { totalCostInRupees } = computeTotalCost(
          classItems,
          "QBExtractor",
          aiModelConfigs,
        );
        const classBatchInfo = getRowBatchInfo(classItems);
        const classProvider = getProviderInfo(classItems);

        tableData.push({
          _id: classLevelId,
          type: "class",
          instructorName: "",
          className: className || "[DELETED]",
          questionBankId: "",
          questionBankName: "",
          questionCount: classTokens.qcount,
          cachedInputTokens: classTokens.cachedInput,
          inputTokens: classTokens.input,
          outputTokens: classTokens.output,
          thoughtsTokens: classTokens.thoughts,
          totalTokens: classTokens.total,
          cost: totalCostInRupees.toFixed(2),
          isExternalBatch: classBatchInfo.isBatch,
          provider: classProvider,
          _rowClassName: classBatchInfo.rowClassName,
          _rowStyle: classBatchInfo.rowStyle,
          level: 1,
          hasChildren: true,
          isExpanded: expandedRows.has(classLevelId),
        });

        if (expandedRows.has(classLevelId)) {
          // Level 2: Question Bank Grouping (Leaf node)
          const qbGroups = groupBy(classItems, "questionBankId");
          Object.entries(qbGroups).forEach(([questionBankId, qbItems]) => {
            const qbTokens = qbItems.reduce(
              (acc, item) => {
                acc.input += item.inputTokenCount;
                acc.cachedInput += item.cachedInputTokenCount ?? 0;
                acc.output += item.outputTokenCount;
                acc.thoughts += item.thoughtsTokenCount ?? 0;
                acc.total += item.totalTokenCount;
                acc.qcount += item.questionCount ?? 0;
                return acc;
              },
              { input: 0, cachedInput: 0, output: 0, thoughts: 0, total: 0, qcount: 0 },
            );
            const { totalCostInRupees } = computeTotalCost(
              qbItems,
              "QBExtractor",
              aiModelConfigs,
            );
            const qbBatchInfo = getRowBatchInfo(qbItems);
            const qbProvider = getProviderInfo(qbItems);

            tableData.push({
              _id: questionBankId,
              type: "questionBank",
              instructorName: "",
              questionBankId: questionBankId,
              questionBankName: qbItems[0].questionBankName || "[DELETED]",
              className: "", // Empty for Question Bank level
              questionCount: qbItems[0].questionCount || 0,
              createdAt: qbItems[0].createdAt,
              cachedInputTokens: qbTokens.cachedInput,
              inputTokens: qbTokens.input,
              outputTokens: qbTokens.output,
              thoughtsTokens: qbTokens.thoughts,
              totalTokens: qbTokens.total,
              cost: totalCostInRupees.toFixed(2),
              isExternalBatch: qbBatchInfo.isBatch,
              provider: qbProvider,
              _rowClassName: qbBatchInfo.rowClassName,
              _rowStyle: qbBatchInfo.rowStyle,
              level: 2,
              hasChildren: false,
              isExpanded: false,
            });
          });
        }
      });
    }
  });

  const columns = [
    {
      key: "instructorName",
      label: "Instructor",
      render: (item) => (
        <div
          className={`hierarchical-cell level-${item.level} ${item.hasChildren ? "expandable" : ""
            }`}
          onClick={() =>
            item.hasChildren &&
            item.type === "instructor" &&
            toggleExpand(item._id)
          }
          style={{ paddingLeft: `${item.level * 20 + 10}px` }}
        >
          {item.hasChildren && item.type === "instructor" && (
            <span
              className={`expand-icon ${item.isExpanded ? "expanded" : ""}`}
            >
              ▶
            </span>
          )}
          <span>{item.instructorName}</span>
        </div>
      ),
    },
    {
      key: "className",
      label: "Class",
      render: (item) => (
        <div
          className={`hierarchical-cell level-${item.level} ${item.hasChildren ? "expandable" : ""
            }`}
          onClick={() =>
            item.hasChildren && item.type === "class" && toggleExpand(item._id)
          }
        >
          {item.type === "class" && item.hasChildren && (
            <span
              className={`expand-icon ${item.isExpanded ? "expanded" : ""}`}
            >
              ▶
            </span>
          )}
          <span>{item.className}</span>
        </div>
      ),
    },
    { key: "questionBankId", label: "QB ID" },
    { key: "questionBankName", label: "QB Name" },
    { key: "questionCount", label: "Question Count" },
    {
      key: "date",
      label: "Date",
      render: (item) => (
        <span>
          {item.createdAt ? new Date(item.createdAt).toLocaleString() : ""}
        </span>
      ),
    },
    {
      key: "cachedInputTokens",
      label: "Cached Input Tokens",
      render: (item) => (
        <span className="token-count input">
          {formatNumber(item.cachedInputTokens ?? 0)}
        </span>
      ),
    },
    {
      key: "inputTokens",
      label: "Input Tokens",
      render: (item) => (
        <span className="token-count input">
          {formatNumber(item.inputTokens)}
        </span>
      ),
    },
    {
      key: "outputTokens",
      label: "Output Tokens",
      render: (item) => (
        <span className="token-count output">
          {formatNumber(item.outputTokens)}
        </span>
      ),
    },
    {
      key: "thoughtsTokens",
      label: "Thoughts Tokens",
      render: (item) => (
        <span className="token-count output">
          {formatNumber(item.thoughtsTokens)}
        </span>
      ),
    },
    {
      key: "totalTokens",
      label: "Total Tokens",
      render: (item) => (
        <span className="token-count total">
          {formatNumber(item.totalTokens)}
        </span>
      ),
    },
    {
      key: "cost",
      label: "Cost (₹)",
      render: (item) => renderCostWithProvider(item),
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
    >
      <TokenUsageLegend />
      <Table
        data={tableData}
        columns={columns}
        caption="Question Bank Extractor Token Usage"
      />
    </motion.div>
  );
};

const ImageGeneratorTable = ({ data, expandedRows, toggleExpand, aiModelConfigs }) => {
  const instructorGroups = groupBy(data, "instructorId");

  const tableData = [];

  Object.entries(instructorGroups).forEach(([instructorId, items]) => {
    // Level 0: Instructor Aggregation
    const instructorItems = items;
    const totalTokens = instructorItems.reduce(
      (acc, item) => {
        acc.input += item.inputTokenCount;
        acc.output += item.outputTokenCount;
        acc.thoughts += item.thoughtsTokenCount ?? 0;
        acc.total += item.totalTokenCount;
        return acc;
      },
      { input: 0, output: 0, thoughts: 0, total: 0 },
    );
    const { totalCostInRupees } = computeTotalCost(
      instructorItems,
      "ImageGenerator",
      aiModelConfigs,
    );

    const instBatchInfo = getRowBatchInfo(instructorItems);
    const instProvider = getProviderInfo(instructorItems);

    tableData.push({
      _id: instructorId,
      type: "instructor",
      instructorName: instructorItems[0].instructorName || "[DELETED]",
      className: "", // Empty for Instructor level
      questionBankName: "",
      questionId: "",
      imageTokenId: "",
      inputTokens: totalTokens.input,
      outputTokens: totalTokens.output,
      thoughtsTokens: totalTokens.thoughts,
      totalTokens: totalTokens.total,
      cost: totalCostInRupees.toFixed(2),
      isExternalBatch: instBatchInfo.isBatch,
      provider: instProvider,
      _rowClassName: instBatchInfo.rowClassName,
      _rowStyle: instBatchInfo.rowStyle,
      level: 0,
      hasChildren: true,
      isExpanded: expandedRows.has(instructorId),
    });

    if (expandedRows.has(instructorId)) {
      // Level 1: Class Grouping
      const classGroups = groupBy(instructorItems, "className");
      Object.entries(classGroups).forEach(([className, classItems]) => {
        const classLevelId = `${instructorId}_${className}`; // Unique ID for Class level

        const classTokens = classItems.reduce(
          (acc, item) => {
            acc.input += item.inputTokenCount;
            acc.output += item.outputTokenCount;
            acc.thoughts += item.thoughtsTokenCount ?? 0;
            acc.total += item.totalTokenCount;
            return acc;
          },
          { input: 0, output: 0, thoughts: 0, total: 0 },
        );
        const { totalCostInRupees } = computeTotalCost(
          classItems,
          "ImageGenerator",
          aiModelConfigs,
        );
        const classBatchInfo = getRowBatchInfo(classItems);
        const classProvider = getProviderInfo(classItems);

        tableData.push({
          _id: classLevelId,
          type: "class",
          instructorName: "",
          className: className || "[DELETED]",
          questionBankName: "",
          questionId: "",
          imageTokenId: "",
          inputTokens: classTokens.input,
          outputTokens: classTokens.output,
          thoughtsTokens: classTokens.thoughts,
          totalTokens: classTokens.total,
          cost: totalCostInRupees.toFixed(2),
          isExternalBatch: classBatchInfo.isBatch,
          provider: classProvider,
          _rowClassName: classBatchInfo.rowClassName,
          _rowStyle: classBatchInfo.rowStyle,
          level: 1,
          hasChildren: true,
          isExpanded: expandedRows.has(classLevelId),
        });

        if (expandedRows.has(classLevelId)) {
          // Level 2: Question Bank Grouping (Under Class)
          const qbGroups = groupBy(classItems, "questionBankId");
          Object.entries(qbGroups).forEach(([questionBankId, qbItems]) => {
            const qbLevelId = `${classLevelId}_${questionBankId}`; // Unique ID for QB level
            const qbTokens = qbItems.reduce(
              (acc, item) => {
                acc.input += item.inputTokenCount;
                acc.output += item.outputTokenCount;
                acc.thoughts += item.thoughtsTokenCount ?? 0;
                acc.total += item.totalTokenCount;
                return acc;
              },
              { input: 0, output: 0, thoughts: 0, total: 0 },
            );
            const { totalCostInRupees } = computeTotalCost(
              qbItems,
              "ImageGenerator",
              aiModelConfigs,
            );
            const qbBatchInfo = getRowBatchInfo(qbItems);
            const qbProvider = getProviderInfo(qbItems);

            tableData.push({
              _id: qbLevelId,
              type: "questionBank",
              instructorName: "",
              questionBankName: qbItems[0].questionBankName || "[DELETED]",
              className: "", // Empty for QB level
              questionId: "",
              imageTokenId: "",
              inputTokens: qbTokens.input,
              outputTokens: qbTokens.output,
              thoughtsTokens: qbTokens.thoughts,
              totalTokens: qbTokens.total,
              cost: totalCostInRupees.toFixed(2),
              isExternalBatch: qbBatchInfo.isBatch,
              provider: qbProvider,
              _rowClassName: qbBatchInfo.rowClassName,
              _rowStyle: qbBatchInfo.rowStyle,
              level: 2,
              hasChildren: true,
              isExpanded: expandedRows.has(qbLevelId),
            });

            if (expandedRows.has(qbLevelId)) {
              // Level 3: Question ID Grouping (Under QB)
              const questionGroups = groupBy(qbItems, "questionId");
              Object.entries(questionGroups).forEach(
                ([questionId, questionItems]) => {
                  const questionLevelId = `${qbLevelId}_${questionId}`; // Unique ID for Question level
                  const questionTokens = questionItems.reduce(
                    (acc, item) => {
                      acc.input += item.inputTokenCount;
                      acc.output += item.outputTokenCount;
                      acc.thoughts += item.thoughtsTokenCount ?? 0;
                      acc.total += item.totalTokenCount;
                      return acc;
                    },
                    { input: 0, output: 0, thoughts: 0, total: 0 },
                  );
                  const { totalCostInRupees } = computeTotalCost(
                    questionItems,
                    "ImageGenerator",
                    aiModelConfigs,
                  );
                  const qBatchInfo = getRowBatchInfo(questionItems);
                  const qProvider = getProviderInfo(questionItems);

                  tableData.push({
                    _id: questionLevelId,
                    type: "question",
                    instructorName: "",
                    questionBankName: "",
                    questionId: questionId,
                    className: "", // Empty for Question level
                    imageTokenId: "",
                    inputTokens: questionTokens.input,
                    outputTokens: questionTokens.output,
                    thoughtsTokens: questionTokens.thoughts,
                    totalTokens: questionTokens.total,
                    cost: totalCostInRupees.toFixed(2),
                    isExternalBatch: qBatchInfo.isBatch,
                    provider: qProvider,
                    _rowClassName: qBatchInfo.rowClassName,
                    _rowStyle: qBatchInfo.rowStyle,
                    level: 3,
                    hasChildren: true,
                    isExpanded: expandedRows.has(questionLevelId),
                  });

                  if (expandedRows.has(questionLevelId)) {
                    // Level 4: Image Token (Leaf Node)
                    questionItems.forEach((item) => {
                      const { totalCostInRupees } = computeTotalCost(
                        [item],
                        "ImageGenerator",
                        aiModelConfigs,
                      );
                      const imgBatchInfo = getRowBatchInfo([item]);
                      const imgProvider = getProviderInfo([item]);

                      tableData.push({
                        _id: item._id,
                        type: "imageToken",
                        instructorName: "",
                        questionBankName: "",
                        questionId: "",
                        className: "", // Empty for Image Token level
                        imageTokenId: item._id,
                        createdAt: item.createdAt,
                        inputTokens: item.inputTokenCount,
                        outputTokens: item.outputTokenCount,
                        thoughtsTokens: item.thoughtsTokenCount,
                        totalTokens: item.totalTokenCount,
                        cost: totalCostInRupees.toFixed(2),
                        isExternalBatch: imgBatchInfo.isBatch,
                        provider: imgProvider,
                        _rowClassName: imgBatchInfo.rowClassName,
                        _rowStyle: imgBatchInfo.rowStyle,
                        level: 4,
                        hasChildren: false,
                        isExpanded: false,
                      });
                    });
                  }
                },
              );
            }
          });
        }
      });
    }
  });

  const columns = [
    {
      key: "instructorName",
      label: "Instructor",
      render: (item) => (
        <div
          className={`hierarchical-cell level-${item.level} ${item.hasChildren ? "expandable" : ""
            }`}
          onClick={() =>
            item.hasChildren &&
            item.type === "instructor" &&
            toggleExpand(item._id)
          }
          style={{ paddingLeft: `${item.level * 20 + 10}px` }}
        >
          {item.hasChildren && item.type === "instructor" && (
            <span
              className={`expand-icon ${item.isExpanded ? "expanded" : ""}`}
            >
              ▶
            </span>
          )}
          <span>{item.instructorName}</span>
        </div>
      ),
    },
    {
      key: "className",
      label: "Class",
      render: (item) => (
        <div
          className={`hierarchical-cell level-${item.level} ${item.hasChildren ? "expandable" : ""
            }`}
          onClick={() =>
            item.hasChildren && item.type === "class" && toggleExpand(item._id)
          }
        >
          {item.type === "class" && item.hasChildren && (
            <span
              className={`expand-icon ${item.isExpanded ? "expanded" : ""}`}
            >
              ▶
            </span>
          )}
          <span>{item.className}</span>
        </div>
      ),
    },
    {
      key: "questionBankName",
      label: "QB Name",
      render: (item) => (
        <div
          className={`hierarchical-cell level-${item.level} ${item.hasChildren ? "expandable" : ""
            }`}
          onClick={() =>
            item.hasChildren &&
            item.type === "questionBank" &&
            toggleExpand(item._id)
          }
        >
          {item.type === "questionBank" && item.hasChildren && (
            <span
              className={`expand-icon ${item.isExpanded ? "expanded" : ""}`}
            >
              ▶
            </span>
          )}
          <span>{item.questionBankName}</span>
        </div>
      ),
    },
    {
      key: "questionId",
      label: "Question ID",
      render: (item) => (
        <div
          className={`hierarchical-cell level-${item.level} ${item.hasChildren ? "expandable" : ""
            }`}
          onClick={() =>
            item.hasChildren &&
            item.type === "question" &&
            toggleExpand(item._id)
          }
        >
          {item.type === "question" && item.hasChildren && (
            <span
              className={`expand-icon ${item.isExpanded ? "expanded" : ""}`}
            >
              ▶
            </span>
          )}
          <span>{item.questionId}</span>
        </div>
      ),
    },
    { key: "imageTokenId", label: "Image Token ID" },
    {
      key: "date",
      label: "Date",
      render: (item) => (
        <span>
          {item.createdAt ? new Date(item.createdAt).toLocaleString() : ""}
        </span>
      ),
    },
    {
      key: "inputTokens",
      label: "Input Tokens",
      render: (item) => (
        <span className="token-count input">
          {formatNumber(item.inputTokens)}
        </span>
      ),
    },
    {
      key: "outputTokens",
      label: "Output Tokens",
      render: (item) => (
        <span className="token-count output">
          {formatNumber(item.outputTokens)}
        </span>
      ),
    },
    {
      key: "thoughtsTokens",
      label: "Thoughts Tokens",
      render: (item) => (
        <span className="token-count output">
          {formatNumber(item.thoughtsTokens)}
        </span>
      ),
    },
    {
      key: "totalTokens",
      label: "Total Tokens",
      render: (item) => (
        <span className="token-count total">
          {formatNumber(item.totalTokens)}
        </span>
      ),
    },
    {
      key: "cost",
      label: "Cost (₹)",
      render: (item) => renderCostWithProvider(item),
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
    >
      <TokenUsageLegend />
      <Table
        data={tableData}
        columns={columns}
        caption="Image Generator Token Usage - Hierarchical View"
      />
    </motion.div>
  );
};

const ScoreSummarizerTable = ({ data, expandedRows, toggleExpand, aiModelConfigs }) => {
  const instructorGroups = groupBy(data, "instructorId");

  const tableData = [];

  Object.entries(instructorGroups).forEach(([instructorId, items]) => {
    // Level 0: Instructor Aggregation
    const instructorItems = items;
    const totalTokens = instructorItems.reduce(
      (acc, item) => {
        acc.input += item.inputTokenCount;
        acc.output += item.outputTokenCount;
        acc.thoughts += item.thoughtsTokenCount ?? 0;
        acc.total += item.totalTokenCount;
        return acc;
      },
      { input: 0, output: 0, thoughts: 0, total: 0 },
    );
    const { totalCostInRupees } = computeTotalCost(
      instructorItems,
      "ScoreSummarizer",
      aiModelConfigs,
    );

    const instBatchInfo = getRowBatchInfo(instructorItems);
    const instProvider = getProviderInfo(instructorItems);

    tableData.push({
      _id: instructorId,
      type: "instructor",
      instructorName: instructorItems[0].instructorName || "[DELETED]",
      testName: "",
      testType: "",
      studentName: "",
      className: "",
      scoreId: "",
      inputTokens: totalTokens.input,
      outputTokens: totalTokens.output,
      thoughtsTokens: totalTokens.thoughts,
      totalTokens: totalTokens.total,
      cost: totalCostInRupees.toFixed(2),
      isExternalBatch: instBatchInfo.isBatch,
      provider: instProvider,
      _rowClassName: instBatchInfo.rowClassName,
      _rowStyle: instBatchInfo.rowStyle,
      level: 0,
      parentId: null,
      hasChildren: true,
      isExpanded: expandedRows.has(instructorId),
    });

    if (expandedRows.has(instructorId)) {
      // Level 1: Class Grouping
      const classGroups = groupBy(instructorItems, "className");
      Object.entries(classGroups).forEach(([className, classItems]) => {
        const classLevelId = `${instructorId}_${className}`;
        const classTokens = classItems.reduce(
          (acc, item) => {
            acc.input += item.inputTokenCount;
            acc.output += item.outputTokenCount;
            acc.thoughts += item.thoughtsTokenCount ?? 0;
            acc.total += item.totalTokenCount;
            return acc;
          },
          { input: 0, output: 0, thoughts: 0, total: 0 },
        );
        const { totalCostInRupees } = computeTotalCost(
          classItems,
          "ScoreSummarizer",
          aiModelConfigs,
        );
        const classBatchInfo = getRowBatchInfo(classItems);
        const classProvider = getProviderInfo(classItems);

        tableData.push({
          _id: classLevelId,
          type: "class",
          instructorName: "",
          testName: "",
          testType: "",
          studentName: "",
          className: className || "[DELETED]",
          scoreId: "",
          inputTokens: classTokens.input,
          outputTokens: classTokens.output,
          thoughtsTokens: classTokens.thoughts,
          totalTokens: classTokens.total,
          cost: totalCostInRupees.toFixed(2),
          isExternalBatch: classBatchInfo.isBatch,
          provider: classProvider,
          _rowClassName: classBatchInfo.rowClassName,
          _rowStyle: classBatchInfo.rowStyle,
          level: 1,
          parentId: instructorId,
          hasChildren: true,
          isExpanded: expandedRows.has(classLevelId),
        });

        if (expandedRows.has(classLevelId)) {
          // Level 2: Test Grouping
          const testGroups = groupBy(classItems, "testId");
          Object.entries(testGroups).forEach(([testId, testItems]) => {
            const testLevelId = `${classLevelId}_${testId}`;
            const testTokens = testItems.reduce(
              (acc, item) => {
                acc.input += item.inputTokenCount;
                acc.output += item.outputTokenCount;
                acc.thoughts += item.thoughtsTokenCount ?? 0;
                acc.total += item.totalTokenCount;
                return acc;
              },
              { input: 0, output: 0, thoughts: 0, total: 0 },
            );
            const { totalCostInRupees } = computeTotalCost(
              testItems,
              "ScoreSummarizer",
              aiModelConfigs,
            );
            const testBatchInfo = getRowBatchInfo(testItems);
            const testProvider = getProviderInfo(testItems);

            tableData.push({
              _id: testLevelId,
              type: "test",
              instructorName: "",
              testName: testItems[0].testName || "[DELETED]",
              testType: testItems[0].testType || "",
              className: "",
              studentName: "",
              scoreId: "",
              inputTokens: testTokens.input,
              outputTokens: testTokens.output,
              thoughtsTokens: testTokens.thoughts,
              totalTokens: testTokens.total,
              cost: totalCostInRupees.toFixed(2),
              isExternalBatch: testBatchInfo.isBatch,
              provider: testProvider,
              _rowClassName: testBatchInfo.rowClassName,
              _rowStyle: testBatchInfo.rowStyle,
              level: 2,
              parentId: classLevelId,
              hasChildren: true,
              isExpanded: expandedRows.has(testLevelId),
            });

            if (expandedRows.has(testLevelId)) {
              // Level 3: Student Grouping
              const studentGroups = groupBy(testItems, "studentId");
              Object.entries(studentGroups).forEach(
                ([studentId, studentItems]) => {
                  const studentLevelId = `${testLevelId}_${studentId}`;
                  const studentTokens = studentItems.reduce(
                    (acc, item) => {
                      acc.input += item.inputTokenCount;
                      acc.output += item.outputTokenCount;
                      acc.thoughts += item.thoughtsTokenCount ?? 0;
                      acc.total += item.totalTokenCount;
                      return acc;
                    },
                    { input: 0, output: 0, thoughts: 0, total: 0 },
                  );
                  const { totalCostInRupees } = computeTotalCost(
                    studentItems,
                    "ScoreSummarizer",
                    aiModelConfigs,
                  );
                  const studentBatchInfo = getRowBatchInfo(studentItems);
                  const studentProvider = getProviderInfo(studentItems);

                  tableData.push({
                    _id: studentLevelId,
                    type: "student",
                    instructorName: "",
                    testName: "",
                    testType: "",
                    studentName: studentItems[0].studentName || "[DELETED]",
                    className: "",
                    scoreId: "",
                    inputTokens: studentTokens.input,
                    outputTokens: studentTokens.output,
                    thoughtsTokens: studentTokens.thoughts,
                    totalTokens: studentTokens.total,
                    cost: totalCostInRupees.toFixed(2),
                    isExternalBatch: studentBatchInfo.isBatch,
                    provider: studentProvider,
                    _rowClassName: studentBatchInfo.rowClassName,
                    _rowStyle: studentBatchInfo.rowStyle,
                    level: 3,
                    parentId: testLevelId,
                    hasChildren: true,
                    isExpanded: expandedRows.has(studentLevelId),
                  });

                  if (expandedRows.has(studentLevelId)) {
                    // Level 4: Score (Leaf Node)
                    studentItems.forEach((item) => {
                      const { totalCostInRupees } = computeTotalCost(
                        [item],
                        "ScoreSummarizer",
                        aiModelConfigs,
                      );
                      const scoreBatchInfo = getRowBatchInfo([item]);
                      const scoreProvider = getProviderInfo([item]);

                      tableData.push({
                        _id: item.scoreId,
                        type: "score",
                        instructorName: "",
                        testName: "",
                        testType: "",
                        studentName: "",
                        scoreId: item.scoreId,
                        createdAt: item.createdAt,
                        className: "",
                        inputTokens: item.inputTokenCount,
                        outputTokens: item.outputTokenCount,
                        thoughtsTokens: item.thoughtsTokenCount,
                        totalTokens: item.totalTokenCount,
                        cost: totalCostInRupees.toFixed(2),
                        isExternalBatch: scoreBatchInfo.isBatch,
                        provider: scoreProvider,
                        _rowClassName: scoreBatchInfo.rowClassName,
                        _rowStyle: scoreBatchInfo.rowStyle,
                        level: 4,
                        parentId: studentLevelId,
                        hasChildren: false,
                        isExpanded: false,
                      });
                    });
                  }
                },
              );
            }
          });
        }
      });
    }
  });

  const columns = [
    {
      key: "instructorName",
      label: "Instructor",
      render: (item) => (
        <div
          className={`hierarchical-cell level-${item.level} ${item.hasChildren ? "expandable" : ""
            }`}
          onClick={() =>
            item.hasChildren &&
            item.type === "instructor" &&
            toggleExpand(item._id)
          }
          style={{ paddingLeft: `${item.level * 20 + 10}px` }}
        >
          {item.hasChildren && item.type === "instructor" && (
            <span
              className={`expand-icon ${item.isExpanded ? "expanded" : ""}`}
            >
              ▶
            </span>
          )}
          <span>{item.instructorName}</span>
        </div>
      ),
    },
    {
      key: "className",
      label: "Class",
      render: (item) => (
        <div
          className={`hierarchical-cell level-${item.level} ${item.hasChildren ? "expandable" : ""
            }`}
          onClick={() =>
            item.hasChildren && item.type === "class" && toggleExpand(item._id)
          }
        >
          {item.type === "class" && item.hasChildren && (
            <span
              className={`expand-icon ${item.isExpanded ? "expanded" : ""}`}
            >
              ▶
            </span>
          )}
          <span>{item.className}</span>
        </div>
      ),
    },
    {
      key: "testName",
      label: "Test",
      render: (item) => (
        <div
          className={`hierarchical-cell level-${item.level} ${item.hasChildren ? "expandable" : ""
            }`}
          onClick={() =>
            item.hasChildren && item.type === "test" && toggleExpand(item._id)
          }
        >
          {item.type === "test" && item.hasChildren && (
            <span
              className={`expand-icon ${item.isExpanded ? "expanded" : ""}`}
            >
              ▶
            </span>
          )}
          <span>
            {item.testName}
            {item.testType ? ` (${item.testType})` : ""}
          </span>
        </div>
      ),
    },
    {
      key: "studentName",
      label: "Student",
      render: (item) => (
        <div
          className={`hierarchical-cell level-${item.level} ${item.hasChildren ? "expandable" : ""
            }`}
          onClick={() =>
            item.hasChildren &&
            item.type === "student" &&
            toggleExpand(item._id)
          }
        >
          {item.type === "student" && item.hasChildren && (
            <span
              className={`expand-icon ${item.isExpanded ? "expanded" : ""}`}
            >
              ▶
            </span>
          )}
          <span>{item.studentName}</span>
        </div>
      ),
    },
    { key: "scoreId", label: "Score ID" },
    {
      key: "date",
      label: "Date",
      render: (item) => (
        <span>
          {item.createdAt ? new Date(item.createdAt).toLocaleString() : ""}
        </span>
      ),
    },

    {
      key: "inputTokens",
      label: "Input Tokens",
      render: (item) => (
        <span className="token-count input">
          {formatNumber(item.inputTokens)}
        </span>
      ),
    },
    {
      key: "outputTokens",
      label: "Output Tokens",
      render: (item) => (
        <span className="token-count output">
          {formatNumber(item.outputTokens)}
        </span>
      ),
    },
    {
      key: "thoughtsTokens",
      label: "Thoughts Tokens",
      render: (item) => (
        <span className="token-count output">
          {formatNumber(item.thoughtsTokens)}
        </span>
      ),
    },
    {
      key: "totalTokens",
      label: "Total Tokens",
      render: (item) => (
        <span className="token-count total">
          {formatNumber(item.totalTokens)}
        </span>
      ),
    },
    {
      key: "cost",
      label: "Cost (₹)",
      render: (item) => renderCostWithProvider(item),
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
    >
      <TokenUsageLegend />
      <Table
        data={tableData}
        columns={columns}
        caption="Performance Report (Score Summarizer) - Hierarchical View"
      />
    </motion.div>
  );
};

const SolutionGeneratorTable = ({ data, expandedRows, toggleExpand, aiModelConfigs }) => {
  const instructorGroups = groupBy(data, "instructorId");

  const tableData = [];

  Object.entries(instructorGroups).forEach(([instructorId, items]) => {
    // Level 0: Instructor Aggregation
    const instructorItems = items;
    const totalTokens = instructorItems.reduce(
      (acc, item) => {
        acc.input += item.inputTokenCount;
        acc.output += item.outputTokenCount;
        acc.thoughts += item.thoughtsTokenCount ?? 0;
        acc.total += item.totalTokenCount;
        acc.qcount += item.questionCount;
        return acc;
      },
      { input: 0, output: 0, thoughts: 0, total: 0, qcount: 0 },
    );
    const { totalCostInRupees } = computeTotalCost(
      instructorItems,
      "SolutionGenerator",
      aiModelConfigs,
    );

    const instBatchInfo = getRowBatchInfo(instructorItems);
    const instProvider = getProviderInfo(instructorItems);

    tableData.push({
      _id: instructorId,
      type: "instructor",
      instructorName: instructorItems[0].instructorName || "[DELETED]",
      className: "", // Empty for Instructor level
      questionBankId: "",
      questionBankName: "",
      questionCount: totalTokens.qcount,
      inputTokens: totalTokens.input,
      outputTokens: totalTokens.output,
      thoughtsTokens: totalTokens.thoughts,
      totalTokens: totalTokens.total,
      cost: totalCostInRupees.toFixed(2),
      isExternalBatch: instBatchInfo.isBatch,
      provider: instProvider,
      _rowClassName: instBatchInfo.rowClassName,
      _rowStyle: instBatchInfo.rowStyle,
      level: 0,
      hasChildren: true,
      isExpanded: expandedRows.has(instructorId),
    });

    if (expandedRows.has(instructorId)) {
      // Level 1: Class Grouping
      const classGroups = groupBy(instructorItems, "className");
      Object.entries(classGroups).forEach(([className, classItems]) => {
        const classLevelId = `${instructorId}_${className}`; // Unique ID for Class level

        const classTokens = classItems.reduce(
          (acc, item) => {
            acc.input += item.inputTokenCount;
            acc.output += item.outputTokenCount;
            acc.thoughts += item.thoughtsTokenCount ?? 0;
            acc.total += item.totalTokenCount;
            acc.qcount += item.questionCount ?? 0;
            return acc;
          },
          { input: 0, output: 0, thoughts: 0, total: 0, qcount: 0 },
        );
        const { totalCostInRupees } = computeTotalCost(
          classItems,
          "SolutionGenerator",
          aiModelConfigs,
        );

        const classBatchInfo = getRowBatchInfo(classItems);
        const classProvider = getProviderInfo(classItems);

        tableData.push({
          _id: classLevelId,
          type: "class",
          instructorName: "",
          className: className || "[DELETED]",
          questionBankId: "",
          questionBankName: "",
          questionCount: classTokens.qcount,
          inputTokens: classTokens.input,
          outputTokens: classTokens.output,
          thoughtsTokens: classTokens.thoughts,
          totalTokens: classTokens.total,
          cost: totalCostInRupees.toFixed(2),
          isExternalBatch: classBatchInfo.isBatch,
          provider: classProvider,
          _rowClassName: classBatchInfo.rowClassName,
          _rowStyle: classBatchInfo.rowStyle,
          level: 1,
          hasChildren: true,
          isExpanded: expandedRows.has(classLevelId),
        });

        if (expandedRows.has(classLevelId)) {
          // Level 2: Question Bank Grouping (Leaf node)
          const qbGroups = groupBy(classItems, "questionBankId");
          Object.entries(qbGroups).forEach(([questionBankId, qbItems]) => {
            const qbTokens = qbItems.reduce(
              (acc, item) => {
                acc.input += item.inputTokenCount;
                acc.output += item.outputTokenCount;
                acc.thoughts += item.thoughtsTokenCount ?? 0;
                acc.total += item.totalTokenCount;
                acc.qcount += item.questionCount ?? 0;
                return acc;
              },
              { input: 0, output: 0, thoughts: 0, total: 0, qcount: 0 },
            );
            const { totalCostInRupees } = computeTotalCost(
              qbItems,
              "SolutionGenerator",
              aiModelConfigs,
            );

            const qbBatchInfo = getRowBatchInfo(qbItems);
            const qbProvider = getProviderInfo(qbItems);

            tableData.push({
              _id: questionBankId,
              type: "questionBank",
              instructorName: "",
              questionBankId: questionBankId,
              questionBankName: qbItems[0].questionBankName || "[DELETED]",
              className: "", // Empty for Question Bank level
              questionCount: qbItems[0].questionCount || 0,
              createdAt: qbItems[0].createdAt,
              inputTokens: qbTokens.input,
              outputTokens: qbTokens.output,
              thoughtsTokens: qbTokens.thoughts,
              totalTokens: qbTokens.total,
              cost: totalCostInRupees.toFixed(2),
              isExternalBatch: qbBatchInfo.isBatch,
              provider: qbProvider,
              _rowClassName: qbBatchInfo.rowClassName,
              _rowStyle: qbBatchInfo.rowStyle,
              level: 2,
              hasChildren: false,
              isExpanded: false,
            });
          });
        }
      });
    }
  });

  const columns = [
    {
      key: "instructorName",
      label: "Instructor",
      render: (item) => (
        <div
          className={`hierarchical-cell level-${item.level} ${item.hasChildren ? "expandable" : ""
            }`}
          onClick={() =>
            item.hasChildren &&
            item.type === "instructor" &&
            toggleExpand(item._id)
          }
          style={{ paddingLeft: `${item.level * 20 + 10}px` }}
        >
          {item.hasChildren && item.type === "instructor" && (
            <span
              className={`expand-icon ${item.isExpanded ? "expanded" : ""}`}
            >
              ▶
            </span>
          )}
          <span>{item.instructorName}</span>
        </div>
      ),
    },
    {
      key: "className",
      label: "Class",
      render: (item) => (
        <div
          className={`hierarchical-cell level-${item.level} ${item.hasChildren ? "expandable" : ""
            }`}
          onClick={() =>
            item.hasChildren && item.type === "class" && toggleExpand(item._id)
          }
        >
          {item.type === "class" && item.hasChildren && (
            <span
              className={`expand-icon ${item.isExpanded ? "expanded" : ""}`}
            >
              ▶
            </span>
          )}
          <span>{item.className}</span>
        </div>
      ),
    },
    { key: "questionBankId", label: "QB ID" },
    { key: "questionBankName", label: "QB Name" },
    { key: "questionCount", label: "Question Count" },
    {
      key: "date",
      label: "Date",
      render: (item) => (
        <span>
          {item.createdAt ? new Date(item.createdAt).toLocaleString() : ""}
        </span>
      ),
    },
    {
      key: "inputTokens",
      label: "Input Tokens",
      render: (item) => (
        <span className="token-count input">
          {formatNumber(item.inputTokens)}
        </span>
      ),
    },
    {
      key: "outputTokens",
      label: "Output Tokens",
      render: (item) => (
        <span className="token-count output">
          {formatNumber(item.outputTokens)}
        </span>
      ),
    },
    {
      key: "thoughtsTokens",
      label: "Thoughts Tokens",
      render: (item) => (
        <span className="token-count output">
          {formatNumber(item.thoughtsTokens)}
        </span>
      ),
    },
    {
      key: "totalTokens",
      label: "Total Tokens",
      render: (item) => (
        <span className="token-count total">
          {formatNumber(item.totalTokens)}
        </span>
      ),
    },
    {
      key: "cost",
      label: "Cost (₹)",
      render: (item) => renderCostWithProvider(item),
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
    >
      <TokenUsageLegend />
      <Table
        data={tableData}
        columns={columns}
        caption="Solution Generator Token Usage"
      />
    </motion.div>
  );
};

const MCQAnswerPaperParserTable = ({ data, expandedRows, toggleExpand, aiModelConfigs }) => {
  const instructorGroups = groupBy(data, "instructorId");

  const tableData = [];

  Object.entries(instructorGroups).forEach(([instructorId, items]) => {
    // Level 0: Instructor Aggregation
    const instructorItems = items;
    const totalTokens = instructorItems.reduce(
      (acc, item) => {
        acc.input += item.inputTokenCount;
        acc.cachedInput += item.cachedInputTokenCount ?? 0;
        acc.output += item.outputTokenCount;
        acc.thoughts += item.thoughtsTokenCount ?? 0;
        acc.total += item.totalTokenCount;
        acc.filePageCount += item.filePageCount ?? 0;
        return acc;
      },
      { input: 0, cachedInput: 0, output: 0, thoughts: 0, total: 0, filePageCount: 0 },
    );
    const { totalCostInRupees } = computeTotalCost(instructorItems, "MCQAnswerPaperParser", aiModelConfigs);

    const instBatchInfo = getRowBatchInfo(instructorItems);
    const instProvider = getProviderInfo(instructorItems);

    tableData.push({
      _id: instructorId,
      type: "instructor",
      instructorName: instructorItems[0].instructorName || "[DELETED]",
      testName: "",
      testType: "",
      studentName: "",
      className: "",
      answerPaperId: "",
      cachedInputTokens: totalTokens.cachedInput,
      inputTokens: totalTokens.input,
      outputTokens: totalTokens.output,
      thoughtsTokens: totalTokens.thoughts,
      filePageCount: totalTokens.filePageCount,
      totalTokens: totalTokens.total,
      cost: totalCostInRupees.toFixed(2),
      isExternalBatch: instBatchInfo.isBatch,
      provider: instProvider,
      _rowClassName: instBatchInfo.rowClassName,
      _rowStyle: instBatchInfo.rowStyle,
      level: 0,
      parentId: null,
      hasChildren: true,
      isExpanded: expandedRows.has(instructorId),
    });

    if (expandedRows.has(instructorId)) {
      // Level 1: Class Grouping
      const classGroups = groupBy(instructorItems, "className");
      Object.entries(classGroups).forEach(([className, classItems]) => {
        const classLevelId = `${instructorId}_${className}`;
        const classTokens = classItems.reduce(
          (acc, item) => {
            acc.input += item.inputTokenCount;
            acc.cachedInput += item.cachedInputTokenCount ?? 0;
            acc.output += item.outputTokenCount;
            acc.thoughts += item.thoughtsTokenCount ?? 0;
            acc.total += item.totalTokenCount;
            acc.filePageCount += item.filePageCount ?? 0;
            return acc;
          },
          { input: 0, cachedInput: 0, output: 0, thoughts: 0, total: 0, filePageCount: 0 },
        );
        const { totalCostInRupees } = computeTotalCost(classItems, "MCQAnswerPaperParser", aiModelConfigs);

        const classBatchInfo = getRowBatchInfo(classItems);
        const classProvider = getProviderInfo(classItems);

        tableData.push({
          _id: classLevelId,
          type: "class",
          instructorName: "",
          testName: "",
          testType: "",
          studentName: "",
          className: className || "[DELETED]",
          answerPaperId: "",
          cachedInputTokens: classTokens.cachedInput,
          inputTokens: classTokens.input,
          outputTokens: classTokens.output,
          thoughtsTokens: classTokens.thoughts,
          filePageCount: classTokens.filePageCount,
          totalTokens: classTokens.total,
          cost: totalCostInRupees.toFixed(2),
          isExternalBatch: classBatchInfo.isBatch,
          provider: classProvider,
          _rowClassName: classBatchInfo.rowClassName,
          _rowStyle: classBatchInfo.rowStyle,
          level: 1,
          parentId: instructorId,
          hasChildren: true,
          isExpanded: expandedRows.has(classLevelId),
        });

        if (expandedRows.has(classLevelId)) {
          // Level 2: Test Grouping
          const testGroups = groupBy(classItems, "testId");
          Object.entries(testGroups).forEach(([testId, testItems]) => {
            const testLevelId = `${classLevelId}_${testId}`;
            const testTokens = testItems.reduce(
              (acc, item) => {
                acc.input += item.inputTokenCount;
                acc.cachedInput += item.cachedInputTokenCount ?? 0;
                acc.output += item.outputTokenCount;
                acc.thoughts += item.thoughtsTokenCount ?? 0;
                acc.total += item.totalTokenCount;
                acc.filePageCount += item.filePageCount ?? 0;
                return acc;
              },
              { input: 0, cachedInput: 0, output: 0, thoughts: 0, total: 0, filePageCount: 0 },
            );
            const { totalCostInRupees } = computeTotalCost(testItems, "MCQAnswerPaperParser", aiModelConfigs);

            const testBatchInfo = getRowBatchInfo(testItems);
            const testProvider = getProviderInfo(testItems);

            tableData.push({
              _id: testLevelId,
              type: "test",
              instructorName: "",
              testName: testItems[0].testName || "[DELETED]",
              testType: testItems[0].testType || "",
              className: "",
              studentName: "",
              answerPaperId: "",
              cachedInputTokens: testTokens.cachedInput,
              inputTokens: testTokens.input,
              outputTokens: testTokens.output,
              thoughtsTokens: testTokens.thoughts,
              filePageCount: testTokens.filePageCount,
              totalTokens: testTokens.total,
              cost: totalCostInRupees.toFixed(2),
              isExternalBatch: testBatchInfo.isBatch,
              provider: testProvider,
              _rowClassName: testBatchInfo.rowClassName,
              _rowStyle: testBatchInfo.rowStyle,
              level: 2,
              parentId: classLevelId,
              hasChildren: true,
              isExpanded: expandedRows.has(testLevelId),
            });

            if (expandedRows.has(testLevelId)) {
              // Level 3: Student (leaf node)
              const studentGroups = groupBy(testItems, "studentId");
              Object.entries(studentGroups).forEach(([studentId, studentItems]) => {
                const studentLevelId = `${testLevelId}_${studentId}`;
                const studentTokens = studentItems.reduce(
                  (acc, item) => {
                    acc.input += item.inputTokenCount;
                    acc.cachedInput += item.cachedInputTokenCount ?? 0;
                    acc.output += item.outputTokenCount;
                    acc.thoughts += item.thoughtsTokenCount ?? 0;
                    acc.total += item.totalTokenCount;
                    acc.filePageCount += item.filePageCount ?? 0;
                    return acc;
                  },
                  { input: 0, cachedInput: 0, output: 0, thoughts: 0, total: 0, filePageCount: 0 },
                );
                const { totalCostInRupees } = computeTotalCost(studentItems, "MCQAnswerPaperParser", aiModelConfigs);

                const studentBatchInfo = getRowBatchInfo(studentItems);
                const studentProvider = getProviderInfo(studentItems);

                tableData.push({
                  _id: studentLevelId,
                  type: "student",
                  instructorName: "",
                  testName: "",
                  testType: "",
                  studentName: studentItems[0].studentName || "[DELETED]",
                  className: "",
                  answerPaperId: studentItems[0].answerPaperId || "",
                  createdAt: studentItems[0].createdAt,
                  cachedInputTokens: studentTokens.cachedInput,
                  inputTokens: studentTokens.input,
                  outputTokens: studentTokens.output,
                  thoughtsTokens: studentTokens.thoughts,
                  filePageCount: studentTokens.filePageCount,
                  totalTokens: studentTokens.total,
                  cost: totalCostInRupees.toFixed(2),
                  isExternalBatch: studentBatchInfo.isBatch,
                  provider: studentProvider,
                  _rowClassName: studentBatchInfo.rowClassName,
                  _rowStyle: studentBatchInfo.rowStyle,
                  level: 3,
                  parentId: testLevelId,
                  hasChildren: false,
                  isExpanded: false,
                });
              });
            }
          });
        }
      });
    }
  });

  const columns = [
    {
      key: "instructorName",
      label: "Instructor",
      render: (item) => (
        <div
          className={`hierarchical-cell level-${item.level} ${item.hasChildren ? "expandable" : ""}`}
          onClick={() =>
            item.hasChildren && item.type === "instructor" && toggleExpand(item._id)
          }
          style={{ paddingLeft: `${item.level * 20 + 10}px` }}
        >
          {item.hasChildren && item.type === "instructor" && (
            <span className={`expand-icon ${item.isExpanded ? "expanded" : ""}`}>▶</span>
          )}
          <span>{item.instructorName}</span>
        </div>
      ),
    },
    {
      key: "className",
      label: "Class",
      render: (item) => (
        <div
          className={`hierarchical-cell level-${item.level} ${item.hasChildren ? "expandable" : ""}`}
          onClick={() =>
            item.hasChildren && item.type === "class" && toggleExpand(item._id)
          }
        >
          {item.type === "class" && item.hasChildren && (
            <span className={`expand-icon ${item.isExpanded ? "expanded" : ""}`}>▶</span>
          )}
          <span>{item.className}</span>
        </div>
      ),
    },
    {
      key: "testName",
      label: "Test",
      render: (item) => (
        <div
          className={`hierarchical-cell level-${item.level} ${item.hasChildren ? "expandable" : ""}`}
          onClick={() =>
            item.hasChildren && item.type === "test" && toggleExpand(item._id)
          }
        >
          {item.type === "test" && item.hasChildren && (
            <span className={`expand-icon ${item.isExpanded ? "expanded" : ""}`}>▶</span>
          )}
          <span>
            {item.testName}
            {item.testType ? ` (${item.testType})` : ""}
          </span>
        </div>
      ),
    },
    {
      key: "studentName",
      label: "Student",
      render: (item) => (
        <div
          className={`hierarchical-cell level-${item.level} ${item.hasChildren ? "expandable" : ""}`}
        >
          <span>{item.studentName}</span>
        </div>
      ),
    },
    { key: "answerPaperId", label: "Answer Paper ID" },
    {
      key: "date",
      label: "Date",
      render: (item) => (
        <span>{item.createdAt ? new Date(item.createdAt).toLocaleString() : ""}</span>
      ),
    },
    {
      key: "filePageCount",
      label: "Page Count",
      render: (item) => (
        <span className="token-count">{formatNumber(item.filePageCount)}</span>
      ),
    },
    {
      key: "cachedInputTokens",
      label: "Cached Input Tokens",
      render: (item) => (
        <span className="token-count input">{formatNumber(item.cachedInputTokens ?? 0)}</span>
      ),
    },
    {
      key: "inputTokens",
      label: "Input Tokens",
      render: (item) => (
        <span className="token-count input">{formatNumber(item.inputTokens)}</span>
      ),
    },
    {
      key: "outputTokens",
      label: "Output Tokens",
      render: (item) => (
        <span className="token-count output">{formatNumber(item.outputTokens)}</span>
      ),
    },
    {
      key: "thoughtsTokens",
      label: "Thoughts Tokens",
      render: (item) => (
        <span className="token-count output">{formatNumber(item.thoughtsTokens)}</span>
      ),
    },
    {
      key: "totalTokens",
      label: "Total Tokens",
      render: (item) => (
        <span className="token-count total">{formatNumber(item.totalTokens)}</span>
      ),
    },
    {
      key: "cost",
      label: "Cost (₹)",
      render: (item) => renderCostWithProvider(item),
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
    >
      <TokenUsageLegend />
      <Table
        data={tableData}
        columns={columns}
        caption="MCQ Answer Paper Parser - Hierarchical View"
      />
    </motion.div>
  );
};

const UserPromptGeneratorTable = ({
  data,
  expandedRows,
  toggleExpand,
  aiModelConfigs,
}) => {
  const instructorGroups = groupBy(data, "instructorId");

  const tableData = [];

  Object.entries(instructorGroups).forEach(([instructorId, items]) => {
    // Level 0: Instructor Aggregation
    const instructorItems = items;
    const totalTokens = instructorItems.reduce(
      (acc, item) => {
        acc.input += item.inputTokenCount;
        acc.cachedInput += item.cachedInputTokenCount ?? 0;
        acc.output += item.outputTokenCount;
        acc.thoughts += item.thoughtsTokenCount ?? 0;
        acc.total += item.totalTokenCount;
        return acc;
      },
      { input: 0, cachedInput: 0, output: 0, thoughts: 0, total: 0 },
    );
    const { totalCostInRupees } = computeTotalCost(
      instructorItems,
      "UserPromptGenerator",
      aiModelConfigs,
    );

    const instBatchInfo = getRowBatchInfo(instructorItems);
    const instProvider = getProviderInfo(instructorItems);

    tableData.push({
      _id: instructorId,
      type: "instructor",
      instructorName: instructorItems[0].instructorName || "[DELETED]",
      className: "",
      testName: "",
      testType: "",
      cachedInputTokens: totalTokens.cachedInput,
      inputTokens: totalTokens.input,
      outputTokens: totalTokens.output,
      thoughtsTokens: totalTokens.thoughts,
      totalTokens: totalTokens.total,
      cost: totalCostInRupees.toFixed(2),
      isExternalBatch: instBatchInfo.isBatch,
      provider: instProvider,
      _rowClassName: instBatchInfo.rowClassName,
      _rowStyle: instBatchInfo.rowStyle,
      level: 0,
      parentId: null,
      hasChildren: true,
      isExpanded: expandedRows.has(instructorId),
    });

    if (expandedRows.has(instructorId)) {
      // Level 1: Class Grouping
      const classGroups = groupBy(instructorItems, "className");
      Object.entries(classGroups).forEach(([className, classItems]) => {
        const classLevelId = `${instructorId}_${className}`;
        const classTokens = classItems.reduce(
          (acc, item) => {
            acc.input += item.inputTokenCount;
            acc.cachedInput += item.cachedInputTokenCount ?? 0;
            acc.output += item.outputTokenCount;
            acc.thoughts += item.thoughtsTokenCount ?? 0;
            acc.total += item.totalTokenCount;
            return acc;
          },
          { input: 0, cachedInput: 0, output: 0, thoughts: 0, total: 0 },
        );
        const { totalCostInRupees } = computeTotalCost(
          classItems,
          "UserPromptGenerator",
          aiModelConfigs,
        );

        const classBatchInfo = getRowBatchInfo(classItems);
        const classProvider = getProviderInfo(classItems);

        tableData.push({
          _id: classLevelId,
          type: "class",
          instructorName: "",
          className: className || "[DELETED]",
          testName: "",
          testType: "",
          cachedInputTokens: classTokens.cachedInput,
          inputTokens: classTokens.input,
          outputTokens: classTokens.output,
          thoughtsTokens: classTokens.thoughts,
          totalTokens: classTokens.total,
          cost: totalCostInRupees.toFixed(2),
          isExternalBatch: classBatchInfo.isBatch,
          provider: classProvider,
          _rowClassName: classBatchInfo.rowClassName,
          _rowStyle: classBatchInfo.rowStyle,
          level: 1,
          parentId: instructorId,
          hasChildren: true,
          isExpanded: expandedRows.has(classLevelId),
        });

        if (expandedRows.has(classLevelId)) {
          // Level 2: Test Grouping
          const testGroups = groupBy(classItems, "testId");
          Object.entries(testGroups).forEach(([testId, testItems]) => {
            const testLevelId = `${classLevelId}_${testId}`;
            const testTokens = testItems.reduce(
              (acc, item) => {
                acc.input += item.inputTokenCount;
                acc.cachedInput += item.cachedInputTokenCount ?? 0;
                acc.output += item.outputTokenCount;
                acc.thoughts += item.thoughtsTokenCount ?? 0;
                acc.total += item.totalTokenCount;
                return acc;
              },
              { input: 0, cachedInput: 0, output: 0, thoughts: 0, total: 0 },
            );
            const { totalCostInRupees } = computeTotalCost(
              testItems,
              "UserPromptGenerator",
              aiModelConfigs,
            );

            const testBatchInfo = getRowBatchInfo(testItems);
            const testProvider = getProviderInfo(testItems);
            const hasMultipleAttempts = testItems.length > 1;

            tableData.push({
              _id: testLevelId,
              type: "test",
              instructorName: "",
              className: "",
              testName: testItems[0].testName || "[DELETED]",
              testType: testItems[0].testType || "",
              createdAt: hasMultipleAttempts ? null : testItems[0].createdAt,
              cachedInputTokens: testTokens.cachedInput,
              inputTokens: testTokens.input,
              outputTokens: testTokens.output,
              thoughtsTokens: testTokens.thoughts,
              totalTokens: testTokens.total,
              cost: totalCostInRupees.toFixed(2),
              isExternalBatch: testBatchInfo.isBatch,
              provider: testProvider,
              _rowClassName: testBatchInfo.rowClassName,
              _rowStyle: testBatchInfo.rowStyle,
              level: 2,
              parentId: classLevelId,
              hasChildren: hasMultipleAttempts,
              isExpanded: hasMultipleAttempts && expandedRows.has(testLevelId),
            });

            if (hasMultipleAttempts && expandedRows.has(testLevelId)) {
              // Level 3: Individual Generation Attempts
              testItems.forEach((item, index) => {
                const attemptLevelId = `${testLevelId}_attempt_${item._id || index}`;
                const { totalCostInRupees } = computeTotalCost(
                  [item],
                  "UserPromptGenerator",
                  aiModelConfigs,
                );
                const itemBatchInfo = getRowBatchInfo([item]);
                const itemProvider = getProviderInfo([item]);

                tableData.push({
                  _id: attemptLevelId,
                  type: "attempt",
                  instructorName: "",
                  className: "",
                  testName: `Generation #${index + 1}`,
                  testType: item.passNumber ? `Pass ${item.passNumber}` : "",
                  createdAt: item.createdAt,
                  cachedInputTokens: item.cachedInputTokenCount ?? 0,
                  inputTokens: item.inputTokenCount ?? 0,
                  outputTokens: item.outputTokenCount ?? 0,
                  thoughtsTokens: item.thoughtsTokenCount ?? 0,
                  totalTokens: item.totalTokenCount ?? 0,
                  cost: totalCostInRupees.toFixed(2),
                  isExternalBatch: itemBatchInfo.isBatch,
                  provider: itemProvider,
                  _rowClassName: itemBatchInfo.rowClassName,
                  _rowStyle: itemBatchInfo.rowStyle,
                  level: 3,
                  parentId: testLevelId,
                  hasChildren: false,
                  isExpanded: false,
                });
              });
            }
          });
        }
      });
    }
  });

  const columns = [
    {
      key: "instructorName",
      label: "Instructor",
      render: (item) => (
        <div
          className={`hierarchical-cell level-${item.level} ${
            item.hasChildren ? "expandable" : ""
          }`}
          onClick={() =>
            item.hasChildren &&
            item.type === "instructor" &&
            toggleExpand(item._id)
          }
          style={{ paddingLeft: `${item.level * 20 + 10}px` }}
        >
          {item.hasChildren && item.type === "instructor" && (
            <span
              className={`expand-icon ${item.isExpanded ? "expanded" : ""}`}
            >
              ▶
            </span>
          )}
          <span>{item.instructorName}</span>
        </div>
      ),
    },
    {
      key: "className",
      label: "Class",
      render: (item) => (
        <div
          className={`hierarchical-cell level-${item.level} ${
            item.hasChildren ? "expandable" : ""
          }`}
          onClick={() =>
            item.hasChildren && item.type === "class" && toggleExpand(item._id)
          }
        >
          {item.type === "class" && item.hasChildren && (
            <span
              className={`expand-icon ${item.isExpanded ? "expanded" : ""}`}
            >
              ▶
            </span>
          )}
          <span>{item.className}</span>
        </div>
      ),
    },
    {
      key: "testName",
      label: "Test",
      render: (item) => (
        <div
          className={`hierarchical-cell level-${item.level} ${
            item.hasChildren ? "expandable" : ""
          }`}
          onClick={() =>
            item.hasChildren && item.type === "test" && toggleExpand(item._id)
          }
        >
          {item.type === "test" && item.hasChildren && (
            <span
              className={`expand-icon ${item.isExpanded ? "expanded" : ""}`}
            >
              ▶
            </span>
          )}
          <span>
            {item.testName}
            {item.testType ? ` (${item.testType})` : ""}
          </span>
        </div>
      ),
    },
    {
      key: "date",
      label: "Date",
      render: (item) => (
        <span>
          {item.createdAt ? new Date(item.createdAt).toLocaleString() : ""}
        </span>
      ),
    },
    {
      key: "cachedInputTokens",
      label: "Cached Input Tokens",
      render: (item) => (
        <span className="token-count input">
          {formatNumber(item.cachedInputTokens ?? 0)}
        </span>
      ),
    },
    {
      key: "inputTokens",
      label: "Input Tokens",
      render: (item) => (
        <span className="token-count input">
          {formatNumber(item.inputTokens)}
        </span>
      ),
    },
    {
      key: "outputTokens",
      label: "Output Tokens",
      render: (item) => (
        <span className="token-count output">
          {formatNumber(item.outputTokens)}
        </span>
      ),
    },
    {
      key: "thoughtsTokens",
      label: "Thoughts Tokens",
      render: (item) => (
        <span className="token-count output">
          {formatNumber(item.thoughtsTokens)}
        </span>
      ),
    },
    {
      key: "totalTokens",
      label: "Total Tokens",
      render: (item) => (
        <span className="token-count total">
          {formatNumber(item.totalTokens)}
        </span>
      ),
    },
    {
      key: "cost",
      label: "Cost (₹)",
      render: (item) => renderCostWithProvider(item),
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
    >
      <TokenUsageLegend />
      <Table
        data={tableData}
        columns={columns}
        caption="User Prompt Generator - Hierarchical View"
      />
    </motion.div>
  );
};

